import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PlusIcon,
  SparklesIcon,
  StopIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useTaskSubscription } from '../hooks/useTaskSubscription';
import { CatalogEditModal, ModalMode, HistoryVersion } from '../components/CatalogEditModal';
import type { OutlineItem } from '../types';
import {
  analysisApi,
  AnalysisRequirement,
  DocumentAnalysisSnapshot,
  DurableOutlineNode,
  documentApi,
  getErrorMessage,
  OutlineAIOperation,
  OutlineOperationTemplate,
  OutlineVersionRecord,
  OutlineWorkspaceState,
  outlineWorkspaceApi,
  PersistedTaskItem,
  PersistedTaskSnapshot,
  taskApi,
  TemplateItem,
} from '../services/api';
import { saveAs } from 'file-saver';
import { makeIdempotencyKey } from '../utils/idempotencyKey';

interface DurableOutlineWorkspaceProps {
  projectId?: number | null;
  onConfirmed?: () => void;
}

/** 与数据库 bid_outline_template (type='style') 对齐的完整风格值。 */
type CandidateStyle =
  | 'general'
  | 'concise'
  | 'flat'
  | 'deep'
  | 'professional'
  | 'plain'
  | 'pain_point'
  | 'process'
  | 'responsive'
  | 'highlight';
type OperationType = 'optimize' | 'expand' | 'manual_idea';

const STYLE_LABELS: Record<CandidateStyle, string> = {
  general: '通用风格',
  concise: '简洁风格',
  flat: '平铺风格',
  deep: '深度风格',
  professional: '专业风格',
  plain: '朴实风格',
  pain_point: '痛点风格',
  process: '流程风格',
  responsive: '响应风格',
  highlight: '亮点风格',
};

const ACTIVE_TASK_STATUSES = new Set([0, 10, 20, 60, 80]);
const FAILED_TASK_STATUSES = new Set([40, 50]);

const operationLabels: Record<OperationType, string> = {
  optimize: '智能优化',
  expand: '智能扩写',
  manual_idea: '编写思路',
};

const operationStatusLabels: Record<number, string> = {
  0: '等待处理',
  10: '处理中',
  20: '待采用',
  30: '已采用',
  40: '已放弃',
  50: '失败',
};

const sourceTypeLabels: Record<string, string> = {
  MANUAL_EDIT: '人工编辑',
  AI_CANDIDATE: '三套候选',
  AI_OPTIMIZE: '智能优化',
  AI_EXPAND: '智能扩写',
  AI_MANUAL_IDEA: '编写思路',
  RESTORE: '历史恢复',
};

const makeOutlineId = () => {
  const suffix = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `outline-${suffix}`;
};

const cloneOutline = (outline: DurableOutlineNode[]) =>
  JSON.parse(JSON.stringify(outline)) as DurableOutlineNode[];

const requirementsAsText = (requirements: AnalysisRequirement[]) =>
  requirements
    .map((item, index) => {
      const score = item.score_text ? `（${item.score_text}）` : '';
      return `${index + 1}. ${item.title}${score}\n${item.description}`.trim();
    })
    .join('\n\n');

const makeEmptyNode = (): DurableOutlineNode => ({
  id: makeOutlineId(),
  title: '新目录',
  description: '',
  expected_word_count: 0,
  source_requirement_id: '',
  source_requirement_title: '',
  children: [],
});

const toWritableOutline = (
  outline: DurableOutlineNode[]
): DurableOutlineNode[] => outline.map((node) => ({
  id: node.id,
  title: node.title.trim(),
  description: node.description || '',
  expected_word_count: Number(node.expected_word_count) || 0,
  source_requirement_id: node.source_requirement_id || '',
  source_requirement_title: node.source_requirement_title || '',
  children: toWritableOutline(node.children || []),
}));

const updateOutlineNode = (
  outline: DurableOutlineNode[],
  nodeId: string,
  update: Partial<DurableOutlineNode>
): DurableOutlineNode[] => outline.map((node) => (
  node.id === nodeId
    ? { ...node, ...update }
    : {
      ...node,
      children: updateOutlineNode(node.children || [], nodeId, update),
    }
));

const appendChildNode = (
  outline: DurableOutlineNode[],
  parentId: string,
  child: DurableOutlineNode
): DurableOutlineNode[] => outline.map((node) => (
  node.id === parentId
    ? { ...node, children: [...(node.children || []), child] }
    : {
      ...node,
      children: appendChildNode(node.children || [], parentId, child),
    }
));

const removeOutlineNode = (
  outline: DurableOutlineNode[],
  nodeId: string
): DurableOutlineNode[] => outline
  .filter((node) => node.id !== nodeId)
  .map((node) => ({
    ...node,
    children: removeOutlineNode(node.children || [], nodeId),
  }));

/** 交换同一父节点下两个兄弟节点的位置（上下移动）。
 * direction: -1=上移（与前一个交换）, 1=下移（与后一个交换）。
 */
const swapOutlineNode = (
  outline: DurableOutlineNode[],
  nodeId: string,
  direction: -1 | 1
): DurableOutlineNode[] => {
  // 先尝试在顶层找
  const idx = outline.findIndex((n) => n.id === nodeId);
  if (idx >= 0) {
    const target = idx + direction;
    if (target < 0 || target >= outline.length) return outline; // 边界
    const result = [...outline];
    [result[idx], result[target]] = [result[target], result[idx]];
    return result;
  }
  // 递归到 children 里找
  return outline.map((node) => ({
    ...node,
    children: swapOutlineNode(node.children || [], nodeId, direction),
  }));
};

/** 找到目标节点后，对其所有后代按当前数组顺序重新分配 sort_order。
 * 后端 save_tree 会按兄弟节点数组顺序 enumerate 自动分配 sort_order，
 * 所以这里只需确保前端 draft 里每个节点的 sort_order 与其在兄弟数组的
 * index 一致即可——保存后后端就会落库。
 */
const reorderOutlineSubtree = (
  outline: DurableOutlineNode[],
  targetId: string
): DurableOutlineNode[] => {
  const visit = (nodes: DurableOutlineNode[], hit: boolean): DurableOutlineNode[] => {
    return nodes.map((node, idx) => {
      const children = visit(node.children || [], hit || node.id === targetId);
      // 目标节点本身 + 它的所有后代都重新分配 sort_order
      if (hit) {
        return { ...node, sort_order: idx, children };
      }
      // 当前节点命中 → 它自己和它的 children 都要重排
      if (node.id === targetId) {
        return { ...node, sort_order: idx, children };
      }
      return { ...node, children };
    });
  };
  return visit(outline, false);
};

const collectNodeIds = (outline: DurableOutlineNode[]): string[] =>
  outline.flatMap((node) => [
    node.id,
    ...collectNodeIds(node.children || []),
  ]);

const findNodePath = (
  outline: DurableOutlineNode[],
  nodeId: string,
  parents: string[] = []
): string[] => {
  for (const node of outline) {
    if (node.id === nodeId) return [...parents, node.id];
    const childPath = findNodePath(
      node.children || [],
      nodeId,
      [...parents, node.id]
    );
    if (childPath.length > 0) return childPath;
  }
  return [];
};

const countOutline = (outline: DurableOutlineNode[]) => {
  let nodes = 0;
  let leaves = 0;
  const visit = (items: DurableOutlineNode[]) => {
    items.forEach((item) => {
      nodes += 1;
      if (item.children?.length) {
        visit(item.children);
      } else {
        leaves += 1;
      }
    });
  };
  visit(outline);
  return { nodes, leaves };
};

const formatTime = (value: number) => {
  if (!value) return '-';
  return new Date(value * 1000).toLocaleString('zh-CN', {
    hour12: false,
  });
};

const isContentImpactConflict = (error: unknown) => {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: { detail?: unknown; msg?: string };
      };
    }
  )?.response;
  if (response?.status !== 409) {
    return false;
  }
  const detail = String(response.data?.msg || '');
  return detail.includes('正文') || detail.includes('影响');
};

const taskTone = (status: number) => {
  if (status === 30) return 'bg-emerald-50 text-emerald-700';
  if (FAILED_TASK_STATUSES.has(status) || status === 70) {
    return 'bg-red-50 text-red-700';
  }
  return 'bg-blue-50 text-blue-700';
};

interface OutlinePreviewProps {
  outline: DurableOutlineNode[];
  compact?: boolean;
  editable?: boolean;
  editingNodeId?: string | null;
  editingNodeTitle?: string;
  savingNodeId?: string | null;
  onNodeStartEdit?: (nodeId: string, currentTitle: string) => void;
  onNodeTitleChange?: (nodeId: string, value: string) => void;
  onNodeSave?: (nodeId: string) => void;
  onNodeKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>, nodeId: string) => void;
}

const OutlinePreview: React.FC<OutlinePreviewProps> = ({
  outline,
  compact = false,
  editable = false,
  editingNodeId = null,
  editingNodeTitle = '',
  savingNodeId = null,
  onNodeStartEdit,
  onNodeTitleChange,
  onNodeSave,
  onNodeKeyDown,
}) => {
  const renderNodeTitle = (node: DurableOutlineNode) => {
    if (editable && editingNodeId === node.id) {
      return (
        <input
          type="text"
          value={editingNodeTitle}
          onChange={(e) => onNodeTitleChange && onNodeTitleChange(node.id, e.target.value)}
          onBlur={() => onNodeSave && onNodeSave(node.id)}
          onKeyDown={(e) => onNodeKeyDown && onNodeKeyDown(e, node.id)}
          autoFocus
          maxLength={200}
          className="w-full rounded border border-blue-300 bg-white px-2 py-0.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-200"
        />
      );
    }
    if (editable) {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNodeStartEdit && onNodeStartEdit(node.id, node.title);
          }}
          title="点击编辑标题"
          className={`block text-left cursor-text rounded px-1 -mx-1 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
            savingNodeId === node.id ? 'opacity-60' : ''
          }`}
        >
          {node.title}
        </button>
      );
    }
    return (
      <>
        {node.title}
        {!compact && node.expected_word_count > 0 && (
          <span className="ml-2 text-xs text-slate-400">
            {node.expected_word_count} 字
          </span>
        )}
        {savingNodeId === node.id && (
          <span className="ml-1 text-xs text-blue-500">保存中...</span>
        )}
      </>
    );
  };

  const renderFlatList = (nodes: DurableOutlineNode[], level: number) => {
    return nodes.map((node) => (
      <div key={node.id} className={level > 0 ? 'ml-4' : ''}>
        <div className="flex items-start gap-2 text-sm text-slate-700">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
          <span>{renderNodeTitle(node)}</span>
        </div>
        {node.children?.length > 0 && (
          <div className="mt-1 border-l border-slate-100 pl-3">
            {renderFlatList(node.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="space-y-3">
      {outline.map((topNode) => (
        <div
          key={topNode.id}
          className={`rounded-lg border ${
            compact
              ? 'border-slate-200 bg-white'
              : 'border-slate-100 bg-slate-50/30'
          } p-3`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-2">
              <span className="text-sm font-semibold text-slate-800">
                {renderNodeTitle(topNode)}
              </span>
              {compact && topNode.expected_word_count > 0 && (
                <span className="text-xs text-slate-400">
                  {topNode.expected_word_count} 字
                </span>
              )}
            </div>
          </div>
          {topNode.children?.length > 0 && (
            <div className="mt-2 space-y-1">
              {renderFlatList(topNode.children, 1)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

interface EditorNodeProps {
  node: DurableOutlineNode;
  depth: number;
  index?: number;
  selected: Set<string>;
  onSelect: (nodeId: string) => void;
  onChange: (nodeId: string, update: Partial<DurableOutlineNode>) => void;
  onAddChild: (nodeId: string) => void;
  onRemove: (nodeId: string, title: string) => void;
  onSaveMergedIdea?: (nodeId: string, mergedContent: string) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onAIOptimize?: (nodeId: string) => void;
  onAIExpand?: (nodeId: string) => void;
  recommendedPages?: number;
  wordCountRange?: { min: number; max: number };
}

interface ReadOnlyTreeNodeProps {
  node: any;
  depth: number;
  selected: Set<string>;
  onSelect: (nodeId: string) => void;
}

// 独立的只读目录节点组件：每层有自己的展开状态，depth<3 默认展开
const ReadOnlyTreeNode: React.FC<ReadOnlyTreeNodeProps> = ({ node, depth, selected, onSelect }) => {
  const [expanded, setExpanded] = React.useState(depth < 3);
  const hasSubChildren = node.children && node.children.length > 0;
  const symbol = depth === 0 ? '◆' : '◇';
  const titleClass = depth === 0 ? 'font-medium text-slate-700' : 'text-slate-600';
  return (
    <div className="py-2">
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={selected.has(node.id)}
          onChange={() => onSelect(node.id)}
          className="mt-1.5 rounded border-slate-300 text-primary focus:ring-primary"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-sm ${titleClass}`}>{symbol} {node.title}</span>
            <span className="text-xs text-slate-400">
              {node.expected_word_count > 0 ? `${node.expected_word_count}字` : '-'}
            </span>
          </div>
          {node.description && (
            <div className="mt-0.5 text-xs text-slate-500">{node.description}</div>
          )}
        </div>
        <div className="flex shrink-0 gap-0.5">
          {hasSubChildren && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="rounded p-1 text-slate-400 hover:bg-slate-100"
              title="展开/收起子级"
            >
              {expanded ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>
      {expanded && hasSubChildren && (
        <div className="ml-4 mt-1 border-l-2 border-slate-100 pl-2">
          {node.children.map((child: any) => (
            <ReadOnlyTreeNode key={child.id} node={child} depth={depth + 1} selected={selected} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
};

const EditorNode: React.FC<EditorNodeProps> = ({
  node,
  depth,
  index,
  selected,
  onSelect,
  onChange,
  onAddChild,
  onRemove,
  onSaveMergedIdea,
  onMoveUp,
  onMoveDown,
  onAIOptimize,
  onAIExpand,
  recommendedPages,
  wordCountRange,
}) => {
  const [expanded, setExpanded] = useState(depth < 3);
  // depth=0 一级 / depth=1 二级 / depth=2 三级 → 默认展开
  // depth>=3（四级及以下）→ 默认折叠，用户手动展开
  const [mergedEditMode, setMergedEditMode] = useState(false);
  const [mergedContent, setMergedContent] = useState('');
  const [savingMerged, setSavingMerged] = useState(false);
  const [mergedMessage, setMergedMessage] = useState('');
  const hasChildren = node.children.length > 0;
  const isTopLevel = depth === 0;

  // 构建合并内容（一级目录标题+描述+子节点列表，使用层级编号）
  const buildMergedContent = (n: DurableOutlineNode): string => {
    const parts: string[] = [];
    parts.push(`【${n.title}】`);
    if (n.description) {
      parts.push(n.description);
    }
    if (n.children.length > 0) {
      parts.push('');
      // 使用层级编号，如 1. 1.1 1.1.1，不显示"子目录"文字
      const flattenChildren = (children: DurableOutlineNode[], level: number, parentIdx: string) => {
        children.forEach((child, idx) => {
          const prefix = level > 1 ? '  '.repeat(level - 1) : '';
          const idxLabel = parentIdx ? `${parentIdx}.${idx + 1}` : `${idx + 1}`;
          parts.push(`${prefix}${idxLabel} ${child.title}${child.description ? `：${child.description}` : ''}`);
          if (child.children.length > 0) {
            flattenChildren(child.children, level + 1, idxLabel);
          }
        });
      };
      flattenChildren(n.children, 1, '');
    }
    return parts.join('\n');
  };

  const openMergedEdit = () => {
    const existingMerged = (node as DurableOutlineNode & { merged_writing_idea?: string }).merged_writing_idea;
    setMergedContent(existingMerged || buildMergedContent(node));
    setMergedEditMode(true);
    setMergedMessage('');
  };

  const handleSaveMerged = async () => {
    if (!onSaveMergedIdea) return;
    setSavingMerged(true);
    setMergedMessage('');
    try {
      await onSaveMergedIdea(node.id, mergedContent);
      setMergedMessage('保存成功');
      setTimeout(() => setMergedMessage(''), 2000);
      setMergedEditMode(false);
    } catch (err) {
      setMergedMessage(`保存失败：${err instanceof Error ? err.message : '未知错误'}`);
    } finally {
      setSavingMerged(false);
    }
  };


  if (isTopLevel)  if (isTopLevel) {
    const existingMerged = (node as DurableOutlineNode & { merged_writing_idea?: string }).merged_writing_idea;
    const hasMergedIdea = Boolean(existingMerged);

    return (
      <div className="mb-5 overflow-hidden rounded-xl border-2 border-blue-200 bg-white shadow-sm transition-all hover:shadow-md">
        {/* 卡片头部：只读展示 */}
        <div className="flex items-center justify-between border-b border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-3">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={selected.has(node.id)}
              onChange={() => onSelect(node.id)}
              className="rounded border-slate-300 text-[var(--bid-color-primary)] focus:ring-[var(--bid-color-primary)]"
            />
            <div>
              <div className="flex items-center gap-2">
                {isTopLevel && typeof index === 'number' && (
                  <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 text-sm font-semibold text-slate-600">
                    {index + 1}
                  </span>
                )}
                <span className="text-lg font-bold text-slate-800">{node.title}</span>
                {hasMergedIdea && (
                  <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">已合并编辑</span>
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                <span className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-700">一级目录</span>
                <span>{node.expected_word_count > 0 ? `${node.expected_word_count} 字` : '未设置字数'}</span>
                {recommendedPages !== undefined && (
                  <span className="rounded bg-purple-50 px-1.5 py-0.5 text-purple-700">
                    建议 {recommendedPages} 页
                  </span>
                )}
                {wordCountRange && (
                  <span className="text-slate-400">
                    ({wordCountRange.min}-{wordCountRange.max}字)
                  </span>
                )}
                {node.source_requirement_title && (
                  <span>· 评分项: {node.source_requirement_title}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 gap-1">
            {/* 顶部按钮组 - 编写思路相关 */}
            {!mergedEditMode && (
              <>
                <button
                  type="button"
                  onClick={openMergedEdit}
                  className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2.5 py-1 text-xs text-blue-700 hover:bg-blue-100"
                  title="手动编辑编写思路"
                >
                  <PencilIcon className="h-3.5 w-3.5" />
                  手动编辑编写思路
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2.5 py-1 text-xs text-amber-700 hover:bg-amber-100"
                  title="AI优化思路：让AI优化当前章节的编写思路"
                >
                  <SparklesIcon className="h-3.5 w-3.5" />
                  AI优化思路
                </button>
                {isTopLevel && (
                  <>
                    <button
                      type="button"
                      onClick={onMoveUp}
                      disabled={!onMoveUp}
                      className="rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                      title="上移"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={onMoveDown}
                      disabled={!onMoveDown}
                      className="rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                      title="下移"
                    >
                      ↓
                    </button>
                  </>
                )}
              </>
            )}
            {hasChildren && (
              <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                className="inline-flex items-center gap-1 rounded-md bg-white/50 px-2.5 py-1 text-xs text-slate-600 hover:bg-white hover:text-blue-700"
              >
                {expanded ? '收起' : '展开'}
                {expanded ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
              </button>
            )}
            <button onClick={() => onAddChild(node.id)} className="rounded p-1.5 text-slate-400 hover:bg-white hover:text-blue-600" title="添加子项">
              <PlusIcon className="h-5 w-5" />
            </button>
            <button onClick={() => onRemove(node.id, node.title)} className="rounded p-1.5 text-slate-400 hover:bg-white hover:text-red-500" title="删除">
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* 卡片内容：显示/编辑互斥 */}
        {expanded && (
          <div className="px-5 py-4">
            {/* 编辑模式：只显示编辑框 */}
            {mergedEditMode ? (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-500">
                    {hasMergedIdea
                      ? '编辑编写思路（本大章节的整体编写思路，将用于正文生成）'
                      : '合并编辑（一级目录标题+描述+子节点内容作为整体编辑）'}
                  </label>
                  {mergedMessage && (
                    <span className={`text-xs ${mergedMessage.includes('成功') ? 'text-green-600' : 'text-red-600'}`}>
                      {mergedMessage}
                    </span>
                  )}
                </div>
                <textarea
                  value={mergedContent}
                  onChange={(e) => setMergedContent(e.target.value)}
                  rows={12}
                  className="w-full rounded-lg border-2 border-blue-200 bg-blue-50/30 px-3 py-2 text-sm text-slate-700 focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-100 font-mono"
                  placeholder={hasMergedIdea
                    ? '编辑本大章节的编写思路（AI 生成内容可直接修改）...'
                    : '编辑合并后的整体内容（包含一级目录标题、描述和子节点列表）...'}
                />
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-slate-400">
                    提示：编辑后保存，正文生成时将优先使用此合并内容
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={savingMerged}
                      onClick={() => {
                        setMergedEditMode(false);
                        setMergedMessage('');
                      }}
                      className="rounded-lg border border-slate-200 px-4 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      disabled={savingMerged}
                      onClick={handleSaveMerged}
                      className="rounded-lg bg-blue-500 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-600 disabled:opacity-50"
                    >
                      {savingMerged ? '保存中...' : (hasMergedIdea ? '保存思路' : '保存合并内容')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* 显示模式：只读展示 */
              <>
                {/* 已保存合并内容：只显示合并内容预览，不再重复显示description和子节点 */}
                {hasMergedIdea ? (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-xs font-medium text-green-700">已保存的合并编辑内容</span>
                      <button
                        type="button"
                        onClick={openMergedEdit}
                        className="text-xs text-green-600 hover:underline"
                      >
                        重新编辑
                      </button>
                    </div>
                    <div className="text-xs text-slate-600 whitespace-pre-wrap">
                      {existingMerged}
                    </div>
                  </div>
                ) : (
                  /* 未保存合并内容：显示原始目录结构 */
                  <>
                    {/* 一级目录描述（只读） */}
                    {node.description && (
                      <div className="mb-3">
                        <div className="mb-1 text-xs font-medium text-slate-500">章节写作说明</div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 whitespace-pre-wrap">
                          {node.description}
                        </div>
                      </div>
                    )}

                    {/* 子节点：只读展示 */}
                    {hasChildren ? (
                      <div className="border-t border-slate-100 pt-3">
                        <div className="mb-2 text-xs font-medium text-slate-500">子目录结构</div>
                        <div className="space-y-0.5">
                          {node.children.map((child: any) => (
                            <ReadOnlyTreeNode key={child.id} node={child} depth={1} selected={selected} onSelect={onSelect} />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">
                        暂无子章节，点击"添加子项"创建
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
        {/* 底部按钮组 - AI目录操作 */}
          {!mergedEditMode && (
            <div className="flex items-center justify-end gap-2 border-t border-blue-100 bg-blue-50/30 px-4 py-2">
              <button
                type="button"
                onClick={() => onAIExpand?.(node.id)}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-3 py-1.5 text-xs text-emerald-700 hover:bg-emerald-100"
                title="AI扩写目录：让AI为当前章节生成更详细的子目录"
              >
                <SparklesIcon className="h-3.5 w-3.5" />
                AI扩写目录
              </button>
              <button
                type="button"
                onClick={() => onAIOptimize?.(node.id)}
                className="inline-flex items-center gap-1 rounded-md bg-violet-50 px-3 py-1.5 text-xs text-violet-700 hover:bg-violet-100"
                title="AI优化目录：让AI优化当前章节的目录结构"
              >
                <SparklesIcon className="h-3.5 w-3.5" />
                AI优化目录
              </button>
            </div>
          )}
      </div>
    );
  }

  return null;
};


const DurableOutlineWorkspace: React.FC<
  DurableOutlineWorkspaceProps
> = ({ projectId, onConfirmed }) => {
  const [workspace, setWorkspace] =
    useState<OutlineWorkspaceState | null>(null);
  const [draft, setDraft] = useState<DurableOutlineNode[]>([]);
  const [analysisVersion, setAnalysisVersion] = useState(0);
  const [analysisConfirmed, setAnalysisConfirmed] = useState(false);
  const [versions, setVersions] = useState<OutlineVersionRecord[]>([]);
  const [candidateTask, setCandidateTask] =
    useState<PersistedTaskSnapshot | null>(null);
  const [operations, setOperations] = useState<OutlineAIOperation[]>([]);
  const [operationTask, setOperationTask] =
    useState<PersistedTaskSnapshot | null>(null);
  const [templates, setTemplates] = useState<OutlineOperationTemplate[]>([]);
  // 从数据库动态加载的风格模板（bid_outline_template WHERE type='style'）
  const [styleTemplates, setStyleTemplates] = useState<TemplateItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [analysisData, setAnalysisData] = useState<DocumentAnalysisSnapshot | null>(null);
  const [candidateMode, setCandidateMode] =
    useState<'free' | 'aligned'>('free');
  const [candidateStyle, setCandidateStyle] =
    useState<CandidateStyle>('general');
  // 三套候选目录各自独立的编写风格（按候选 index 0/1/2）
  const [candidateStyles, setCandidateStyles] = useState<
    Record<number, CandidateStyle>
  >({ 0: 'general', 1: 'general', 2: 'general' });
  const [candidateInstruction, setCandidateInstruction] = useState('');
  const [candidatePhase, setCandidatePhase] = useState<'ideas' | 'outline'>('ideas');
  const [activeTab, setActiveTab] = useState<'ideas' | 'tender' | 'scoring' | 'overview'>('ideas');
  const [showStyleSelector, setShowStyleSelector] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [currentCandidateIndex, setCurrentCandidateIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [editingCandidateId, setEditingCandidateId] = useState<string | null>(null);
  const [editingCandidateName, setEditingCandidateName] = useState<string>('');
  const [savingCandidateId, setSavingCandidateId] = useState<string | null>(null);
  // 编辑思路相关状态
  const [editingIdeaId, setEditingIdeaId] = useState<string | null>(null);
  const [editingIdeaContent, setEditingIdeaContent] = useState<string>('');
  // 操作记录相关状态
  const [currentOperatingNodeId, setCurrentOperatingNodeId] = useState<string | null>(null);
  // 编辑目录节点标题相关状态
  const [editingOutlineNodeId, setEditingOutlineNodeId] = useState<string | null>(null);
  const [editingOutlineNodeTitle, setEditingOutlineNodeTitle] = useState<string>('');
  const [savingOutlineNodeId, setSavingOutlineNodeId] = useState<string | null>(null);
  // 弹窗相关状态
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('manual_idea');
  const [modalNodeId, setModalNodeId] = useState<string>('');
  const [modalNodeTitle, setModalNodeTitle] = useState<string>('');
  const [modalNodeIndex, setModalNodeIndex] = useState<number>(-1);
  const [modalInitialIdea, setModalInitialIdea] = useState<string>('');
  const [modalInitialCatalog, setModalInitialCatalog] = useState<DurableOutlineNode[]>([]);
  const [ideaHistory, setIdeaHistory] = useState<HistoryVersion[]>([]);
  const [catalogHistory, setCatalogHistory] = useState<OutlineVersionRecord[]>([]);
  const candidateInputReadyRef = useRef(false);
  const adoptingCandidateRef = useRef(false);

  const handleCandidateNameClick = (candidateId: string, currentName: string) => {
    candidateInputReadyRef.current = false;
    setEditingCandidateId(candidateId);
    setEditingCandidateName(currentName);
  };

  const handleCandidateNameSave = async (candidateId: string) => {
    if (adoptingCandidateRef.current) {
      adoptingCandidateRef.current = false;
      setEditingCandidateId(null);
      setEditingCandidateName('');
      return;
    }
    if (!candidateInputReadyRef.current) {
      candidateInputReadyRef.current = true;
      return;
    }
    if (!editingCandidateName.trim() || !projectId) {
      setEditingCandidateId(null);
      setEditingCandidateName('');
      return;
    }
    const newName = editingCandidateName.trim();
    setSavingCandidateId(candidateId);
    try {
      await outlineWorkspaceApi.updateCandidateProfile(projectId, candidateId, newName);
      setCandidateTask((prev) => {
        if (!prev) return prev;
        const updatedItems = (prev.items || []).map((it) => {
          if (it.target_id !== candidateId) return it;
          const plan = (it.content_plan as Record<string, unknown> | null) || {};
          return {
            ...it,
            content_plan: { ...plan, name: newName },
            result_snapshot: {
              ...((it.result_snapshot as Record<string, unknown> | null) || {}),
              name: newName,
            },
          };
        });
        return { ...prev, items: updatedItems };
      });
    } catch {
      /* ignore network error */
    } finally {
      setSavingCandidateId(null);
      setEditingCandidateId(null);
      setEditingCandidateName('');
      candidateInputReadyRef.current = false;
    }
  };

  const handleCandidateNameKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    candidateId: string
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setEditingCandidateId(null);
      setEditingCandidateName('');
      candidateInputReadyRef.current = false;
    }
  };

  const handleCandidateStyleChange = async (
    candidateId: string,
    newStyle: CandidateStyle
  ) => {
    if (!projectId) return;
    setSavingCandidateId(candidateId);
    try {
      // 更新项目设置中的候选方案风格
      await outlineWorkspaceApi.updateProjectCandidateProfile(projectId, candidateId, { style: newStyle });
      // 刷新候选项显示
      setCandidateTask((prev) => {
        if (!prev) return prev;
        const updatedItems = (prev.items || []).map((it) => {
          if (it.target_id !== candidateId) return it;
          const plan = (it.content_plan as Record<string, unknown> | null) || {};
          return {
            ...it,
            content_plan: { ...plan, style: newStyle },
          };
        });
        return { ...prev, items: updatedItems };
      });
      // 重新生成候选项（使用新风格）
      await createCandidates();
    } catch {
      /* ignore network error */
    } finally {
      setSavingCandidateId(null);
    }
  };

  const candidateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingCandidateId && candidateInputRef.current) {
      candidateInputRef.current.focus();
      candidateInputReadyRef.current = true;
    }
  }, [editingCandidateId]);

  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [editingNodeTitle, setEditingNodeTitle] = useState<string>('');
  const [savingNodeId, setSavingNodeId] = useState<string | null>(null);
  const [editingNodeCandidateId, setEditingNodeCandidateId] = useState<string | null>(null);

  const handleNodeStartEdit = (nodeId: string, titleOrValue: string) => {
    setEditingNodeId(nodeId);
    setEditingNodeTitle(titleOrValue);
  };

  const handleNodeTitleChange = (_nodeId: string, value: string) => {
    setEditingNodeTitle(value);
  };

  const handleNodeSave = async (nodeId: string) => {
    if (adoptingCandidateRef.current) {
      adoptingCandidateRef.current = false;
      return;
    }
    if (!editingNodeTitle.trim() || !projectId || !editingNodeCandidateId) {
      setEditingNodeId(null);
      setEditingNodeTitle('');
      return;
    }
    const newTitle = editingNodeTitle.trim();
    setSavingNodeId(nodeId);
    try {
      const response = await outlineWorkspaceApi.updateCandidateNodeTitle(
        projectId,
        editingNodeCandidateId,
        nodeId,
        newTitle
      );
      const updatedOutline = response.data?.data?.outline
        || (response.data as Record<string, unknown>)?.outline;
      if (updatedOutline) {
        setCandidateTask((prev) => {
          if (!prev) return prev;
          const updatedItems = (prev.items || []).map((it) => {
            if (it.target_id !== editingNodeCandidateId) return it;
            const snapshot = (it.result_snapshot as Record<string, unknown> | null) || {};
            return {
              ...it,
              result_snapshot: { ...snapshot, outline: updatedOutline },
            };
          });
          return { ...prev, items: updatedItems };
        });
      }
    } catch {
      /* ignore network error */
    } finally {
      setSavingNodeId(null);
      setEditingNodeId(null);
      setEditingNodeTitle('');
      setEditingNodeCandidateId(null);
    }
  };

  const handleNodeKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    nodeId: string
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setEditingNodeId(null);
      setEditingNodeTitle('');
      setEditingNodeCandidateId(null);
    }
  };

  const [operationType, setOperationType] =
    useState<OperationType>('optimize');
  const [operationTemplate, setOperationTemplate] =
    useState<OutlineOperationTemplate['code']>('structure');
  const [operationStyle, setOperationStyle] =
    useState<CandidateStyle>('general');
  const [operationInstruction, setOperationInstruction] = useState('');
  const [dirty, setDirtyState] = useState(false);
  const dirtyRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [actionKey, setActionKey] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const setDirty = (value: boolean) => {
    dirtyRef.current = value;
    setDirtyState(value);
  };

  const loadWorkspace = useCallback(async () => {
    if (!projectId) {
      setWorkspace(null);
      setDraft([]);
      setIsLoading(false);
      return;
    }

    try {
      setError('');
      const [
        outlineResponse,
        analysisResponse,
        versionsResponse,
        candidatesResponse,
        templatesResponse,
        operationsResponse,
        styleTemplatesResponse,
      ] = await Promise.all([
        outlineWorkspaceApi.get(projectId),
        analysisApi.get(projectId),
        outlineWorkspaceApi.versions(projectId),
        outlineWorkspaceApi.latestCandidates(projectId),
        outlineWorkspaceApi.operationTemplates(projectId),
        outlineWorkspaceApi.operations(projectId),
        outlineWorkspaceApi.getTemplates(projectId),
      ]);

      const nextWorkspace = outlineResponse.data.data;
      const nextAnalysis = analysisResponse.data.data.analysis;
      const nextOperations = operationsResponse.data.data;
      setWorkspace(nextWorkspace);
      if (!dirtyRef.current) {
        setDraft(cloneOutline(nextWorkspace.outline));
      }
      setAnalysisVersion(nextAnalysis?.version_no || 0);
      setAnalysisConfirmed(Boolean(nextAnalysis?.confirmed));
      setAnalysisData(nextAnalysis);
      setVersions(versionsResponse.data.data);
      const loadedTask = candidatesResponse.data.data;
      setCandidateTask(loadedTask);
      // 页面初始化时，从后端返回的 task.items[].content_plan.style 恢复三套候选目录各自的风格
      if (loadedTask?.items && Array.isArray(loadedTask.items)) {
        const restored: Record<number, CandidateStyle> = {
          0: 'general',
          1: 'general',
          2: 'general',
        };
        (loadedTask.items as Array<{ content_plan?: { style?: CandidateStyle } }>).forEach((it, i) => {
          if (it?.content_plan?.style) restored[i] = it.content_plan.style;
        });
        setCandidateStyles(restored);
      }
      setTemplates(templatesResponse.data.data);
      setStyleTemplates(styleTemplatesResponse.data.data?.style || []);
      setOperations(nextOperations);

      const activeOperation = nextOperations.find(
        (operation) =>
          operation.task_id
          && (operation.status === 0 || operation.status === 10)
      );
      if (activeOperation?.task_id) {
        const response = await taskApi.get(activeOperation.task_id)
          .catch(() => null);
        setOperationTask(response?.data.data || null);
      } else {
        setOperationTask(null);
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError, '目录工作台加载失败'));
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // 轻量刷新：只拉取最新 workspace 版本号，避免写操作因闭包里的旧版本号触发 409
  const getFreshWorkspace = useCallback(async (): Promise<OutlineWorkspaceState | null> => {
    if (!projectId) return null;
    try {
      const response = await outlineWorkspaceApi.get(projectId);
      const next = response.data?.data;
      if (next) {
        setWorkspace(next);
      }
      return next ?? null;
    } catch {
      return workspace;
    }
  }, [projectId, workspace]);

  useEffect(() => {
    setDirty(false);
    setSelectedIds(new Set());
    setIsLoading(true);
    void loadWorkspace();
  }, [loadWorkspace]);

  // 自动触发：进入页面且分析已确认时，一次性生成编写思路并回填（不走多候选）
  const applyWritingIdeasRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const ideaApplyStartedRef = useRef(false);
  const createCandidatesRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const modalFullOutlineRef = useRef(false);
  
  useEffect(() => {
    if (isLoading || !projectId || !workspace) return;
    if (!analysisConfirmed || analysisVersion <= 0) return;
    if (ideaApplyStartedRef.current) return; // 每个会话只自动触发一次
    // 已存在回填思路的一级节点则不重复生成
    const alreadyFilled = draft.some(
      (node) => node.merged_writing_idea && String(node.merged_writing_idea).trim()
    );
    if (alreadyFilled) {
      ideaApplyStartedRef.current = true;
      return;
    }
    // 注意：思路不参与目录的多候选任务机制，因此即使存在进行中的候选任务
    // （如旧版遗留的"编写思路生成任务"），也继续触发一次性生成与回填。
    // 延迟1秒等待页面稳定
    const timer = setTimeout(() => {
      ideaApplyStartedRef.current = true;
      // 清掉可能存在的旧 message（如"编写思路生成任务已创建…"），避免误导
      setMessage('');
      void applyWritingIdeasRef.current();
    }, 1000);
    return () => clearTimeout(timer);
  }, [isLoading, projectId, workspace, analysisConfirmed, analysisVersion, draft]);

  const refreshAfterTask = useCallback(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  /**
   * 目录候选 hook 回调：收到 SSE 推送的完整 task 快照时智能合并。
   * 单套装重生成会新建一个只含 1 条 item 的 task B，但 state 里还保留着
   * 之前三套装 task A 的 3 条 items。直接 set 会把另外两套覆盖掉。
   * 这里按 target_id 粒度合并：只覆盖新快照里有的条目，其余保留。
   */
  const mergeCandidateSnapshot = useCallback(
    (snapshot: PersistedTaskSnapshot) => {
      setCandidateTask((prev) => {
        if (!prev) return snapshot;
        const prevItems = (prev.items ?? []) as Array<any>;
        const newItems = (snapshot.items ?? []) as Array<any>;
        if (newItems.length === 0) return snapshot;

        const prevMap = new Map(
          prevItems.map((it) => [it.target_id, it])
        );
        // 用新快照覆盖同名 target_id 的条目，其余保留
        for (const it of newItems) {
          prevMap.set(it.target_id, it);
        }
        const mergedItems = Array.from(prevMap.values());
        // 保持原排序：按 target_id 首次出现顺序（prev + 新增）
        const seen = new Set<string>();
        const ordered: Array<any> = [];
        for (const it of prevItems) {
          const key = it.target_id;
          if (!seen.has(key) && prevMap.has(key)) {
            ordered.push(prevMap.get(key)!);
            seen.add(key);
          }
        }
        for (const it of newItems) {
          const key = it.target_id;
          if (!seen.has(key) && prevMap.has(key)) {
            ordered.push(prevMap.get(key)!);
            seen.add(key);
          }
        }
        return { ...snapshot, items: ordered };
      });
    },
    []
  );

  useTaskSubscription(
    candidateTask,
    mergeCandidateSnapshot,
    refreshAfterTask
  );

  useTaskSubscription(
    operationTask,
    setOperationTask,
    refreshAfterTask
  );

  const outlineStats = useMemo(() => countOutline(draft), [draft]);
  const candidateActive = Boolean(
    candidateTask && ACTIVE_TASK_STATUSES.has(candidateTask.status)
  );
  const operationActive = Boolean(
    operationTask && ACTIVE_TASK_STATUSES.has(operationTask.status)
  );

  // 判断第 idx 套候选目录是否正在重新生成（只看该 item 的 status）
  // 不依赖全局 task.status，因为单套重生成时后端会返回全套 task，
  // 全局 status 是 RUNNING 但其他两套 item 实际还是 SUCCESS
  const isCandidateGenerating = useCallback(
    (idx: number): boolean => {
      if (!candidateTask) return false;
      const items = (candidateTask.items as Array<any> | undefined) ?? [];
      const item = items[idx];
      if (!item) return false; // 没有该 item → 不在生成
      const s = Number(item.status);
      if (s === 30) return false; // SUCCESS
      if (s === 40 || s === 50) return false; // FAILED / CANCELLED
      return true; // PENDING(0) / RUNNING(20) → 正在生成
    },
    [candidateTask]
  );

  // 当前选中的候选方案的 target_id（候选态保存思路时用）
  // 固化态（目录已插入）此值为 undefined，不传给后端
  const activeCandidateId = useMemo(() => {
    const items = (candidateTask?.items as Array<{ target_id?: string }> | undefined) ?? [];
    return items[currentCandidateIndex]?.target_id;
  }, [candidateTask, currentCandidateIndex]);

  // ========== 左侧数据：目录编写思路（一级标题+思路描述）==========
  const ideasData = useMemo(() => {
    // 仅当候选方案存在且未过期（base_project_version 匹配当前 workspace 版本）
    // 时才优先用候选快照；过期候选（如"插入目录"后刷新回来的旧快照）必须
    // 跳过，否则会把已保存的 DB 数据覆盖回旧状态。
    const curVer = workspace?.project_version ?? -1;
    const cBaseVer = Number(candidateTask?.request_snapshot?.base_project_version ?? -1);
    const cStale = candidateTask && cBaseVer !== curVer;
    if (!cStale && candidateTask?.items && candidateTask.items.length > 0) {
      const item = candidateTask.items[currentCandidateIndex];
      const snapshot = (item?.result_snapshot as Record<string, any> | null);
      if (snapshot && snapshot.ideas?.ideas?.length > 0) {
        // 候选态左侧「编写思路」卡片需与右侧目录节点一一对应：使用对应一级
        // 目录节点的稳定 ID 作为思路的 id，否则保存思路时 outline_id 会指向
        // 不存在的节点（如 idea.id 是 "1"），导致后端 404「目录节点不存在」。
        const outlineIds: string[] = Array.isArray(snapshot.outline)
          ? snapshot.outline.map((n: any) => n?.id)
          : [];
        return snapshot.ideas.ideas.map((idea: any, idx: number) => ({
          id: outlineIds[idx] || idea.id || `idea-${idx}`,
          title: idea.title || `第${idx + 1}章`,
          description: idea.description || '',
        }));
      }
    }
    // 降级：用 draft 的 merged_writing_idea（来自 DB）
    if (draft.length > 0) {
      return draft.map((node, idx) => ({
        id: node.id,
        title: node.title,
        description: (node as any).merged_writing_idea || node.description || '',
      }));
    }
    return [];
  }, [candidateTask, currentCandidateIndex, draft, workspace?.project_version]);

  // ========== 右侧数据：完整目录树（含多级子节点）==========
  const outlineData = useMemo(() => {
    // 仅当候选方案未过期时才优先用；过期候选（已插入/已应用）必须跳过，
    // 否则刷新页面会把 DB 里已保存的目录覆盖回旧的候选快照。
    const curVer = workspace?.project_version ?? -1;
    const cBaseVer = Number(candidateTask?.request_snapshot?.base_project_version ?? -1);
    const cStale = candidateTask && cBaseVer !== curVer;
    if (!cStale && candidateTask?.items && candidateTask.items.length > 0) {
      const item = candidateTask.items[currentCandidateIndex];
      const snapshot = (item?.result_snapshot as Record<string, any> | null);
      if (snapshot && snapshot.outline?.length > 0) {
        return snapshot.outline;
      }
    }
    // 没有候选方案或候选方案已过期，使用 draft（来自 DB 的当前目录）
    if (draft.length > 0) {
      return draft;
    }
    return [];
  }, [candidateTask, currentCandidateIndex, draft, workspace?.project_version]);

  const changeNode = (
    nodeId: string,
    update: Partial<DurableOutlineNode>
  ) => {
    setDraft((current) => updateOutlineNode(current, nodeId, update));
    setDirty(true);
  };

  // 上移/下移一级目录节点（按 draft 顺序交换，并写入新 sort_order，标记未保存）
  const moveOutlineNode = (fromIndex: number, dir: -1 | 1) => {
    setDraft((current) => {
      const next = [...current];
      const toIndex = fromIndex + dir;
      if (toIndex < 0 || toIndex >= next.length) return current;
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      // 重新分配 sort_order，保证与顺序一致
      return next.map((node, i) => ({ ...node, sort_order: i }));
    });
    setDirty(true);
  };

  const addRootNode = () => {
    setDraft((current) => [...current, makeEmptyNode()]);
    setDirty(true);
  };

  const addChild = (nodeId: string) => {
    setDraft((current) =>
      appendChildNode(current, nodeId, makeEmptyNode())
    );
    setDirty(true);
  };

  const removeNode = (nodeId: string, title: string) => {
    if (!window.confirm(`确定删除目录“${title}”及其全部子目录吗？`)) {
      return;
    }
    setDraft((current) => removeOutlineNode(current, nodeId));
    setSelectedIds((current) => {
      const next = new Set(current);
      next.delete(nodeId);
      return next;
    });
    setDirty(true);
  };

  const moveNode = (nodeId: string, direction: -1 | 1) => {
    setDraft((current) => swapOutlineNode(current, nodeId, direction));
    setDirty(true);
  };

  // 保存合并编写思路（目录编辑中的整体编辑功能）
  const handleSaveMergedIdea = async (nodeId: string, mergedContent: string): Promise<void> => {
    if (!projectId || !workspace) {
      throw new Error('工作区未初始化');
    }
    // 提交前刷新最新版本号，避免闭包旧值触发 409
    const fresh = await getFreshWorkspace();
    const response = await outlineWorkspaceApi.saveMergedWritingIdea(projectId, {
      base_project_version: (fresh ?? workspace).project_version,
      outline_id: nodeId,
      merged_content: mergedContent,
      // 候选态时只改当前选中那套目录的思路，固化态 activeCandidateId 为空则忽略
      ...(activeCandidateId ? { candidate_id: activeCandidateId } : {}),
    });
    // 更新本地 draft 中的 merged_writing_idea
    setDraft((current) =>
      updateOutlineNode(current, nodeId, {
        merged_writing_idea: mergedContent,
      } as Partial<DurableOutlineNode>)
    );
    // 如果服务器返回了新的状态，更新 workspace
    if (response.data?.data) {
      setWorkspace(response.data.data);
    }
  };

  const toggleSelected = (nodeId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(nodeId)) {
        next.delete(nodeId);
        return next;
      }
      const path = findNodePath(draft, nodeId);
      path.slice(0, -1).forEach((id) => next.delete(id));
      const node = (() => {
        let found: DurableOutlineNode | null = null;
        const visit = (nodes: DurableOutlineNode[]) => {
          nodes.forEach((item) => {
            if (item.id === nodeId) found = item;
            if (!found) visit(item.children || []);
          });
        };
        visit(draft);
        return found;
      })();
      if (node) {
        collectNodeIds((node as DurableOutlineNode).children || [])
          .forEach((id) => next.delete(id));
      }
      next.add(nodeId);
      return next;
    });
  };

  // ========== 左侧编写思路操作 ==========

  // 进入手动编辑思路模式
  const handleManualEditIdea = (ideaId: string, currentContent: string) => {
    setEditingIdeaId(ideaId);
    setEditingIdeaContent(currentContent);
  };

  // 保存手动编辑的思路（使用 PUT /writing-idea 直接保存）
  const handleSaveEditedIdea = async () => {
    if (!editingIdeaId || !projectId || !workspace) return;
    try {
      setMessage('正在保存思路...');
      // 提交前刷新最新版本号，避免闭包旧值触发 409
      const fresh = await getFreshWorkspace();
      await outlineWorkspaceApi.saveMergedWritingIdea(projectId, {
        base_project_version: (fresh ?? workspace).project_version,
        outline_id: editingIdeaId,
        merged_content: editingIdeaContent,
        // 候选态时只改当前选中那套目录的思路
        ...(activeCandidateId ? { candidate_id: activeCandidateId } : {}),
      });
      // 刷新工作区状态
      await loadWorkspace();
      setEditingIdeaId(null);
      setEditingIdeaContent('');
      setMessage('思路已保存');
    } catch (error) {
      setError(getErrorMessage(error, '保存思路失败'));
    }
  };

  // 取消编辑思路
  const handleCancelEditIdea = () => {
    setEditingIdeaId(null);
    setEditingIdeaContent('');
  };

  // ========== 弹窗相关方法 ==========

  // 加载历史版本
  const loadHistoryVersions = async (nodeIdOverride?: string) => {
    if (!projectId) return;
    const targetNodeId = nodeIdOverride ?? modalNodeId;
    try {
      const [versionsResponse, operationsResponse] = await Promise.all([
        outlineWorkspaceApi.versions(projectId),
        outlineWorkspaceApi.operations(projectId),
      ]);
      const versions = versionsResponse.data?.data || [];
      const operations = operationsResponse.data?.data || [];
      setCatalogHistory(versions);

      // 生成思路历史版本
      const ideaVersions: HistoryVersion[] = [];

      // 1) 当前 workspace 的思路（作为"当前版本"）
      if (workspace?.outline) {
        workspace.outline.forEach((node) => {
          if (node.id === targetNodeId && (node as any).merged_writing_idea) {
            ideaVersions.push({
              id: `current-${node.id}`,
              title: '当前版本',
              content: (node as any).merged_writing_idea,
              createdAt: Math.floor(Date.now() / 1000),
            });
          }
        });
      }

      // 2) 从 BidAIOperation 恢复 AI 优化/生成的思路历史（未固化到主分支）
      const ideaOps = operations.filter(
        (op) =>
          (op.operation_type === 'ai_optimize_idea' || op.operation_type === 'ai_generate_idea') &&
          op.target_ids?.includes(targetNodeId)
      );
      for (const op of ideaOps) {
        const snap = (op.result_snapshot || {}) as { optimized_idea?: string; generated_idea?: string };
        const content = snap.optimized_idea || snap.generated_idea;
        if (content && content.trim()) {
          ideaVersions.push({
            id: `ai-${op.id}`,
            title: op.operation_type === 'ai_optimize_idea' ? 'AI 优化版' : 'AI 生成版',
            content,
            createdAt: op.create_time || Math.floor(Date.now() / 1000),
          });
        }
      }

      // 按时间倒序
      ideaVersions.sort((a, b) => b.createdAt - a.createdAt);
      setIdeaHistory(ideaVersions);
    } catch (error) {
      console.error('加载历史版本失败', error);
    }
  };

  // 打开弹窗
  const openModal = async (
    mode: ModalMode,
    nodeId: string,
    nodeTitle: string,
    currentIdea: string,
    currentCatalog: DurableOutlineNode[],
    nodeIndex: number = -1
  ) => {
    setModalMode(mode);
    setModalNodeId(nodeId);
    setModalNodeTitle(nodeTitle);
    setModalNodeIndex(nodeIndex);
    setModalInitialIdea(currentIdea);
    setModalInitialCatalog(currentCatalog);
    // 直接传 nodeId 避免 React setState 竞态导致读到旧 modalNodeId
    await loadHistoryVersions(nodeId);
    setModalOpen(true);
  };

  // 关闭弹窗
  const closeModal = () => {
    setModalOpen(false);
  };

  // 保存思路（弹窗内）
  const handleModalSaveIdea = async (content: string, style?: string, createVersion: boolean = true) => {
    if (!projectId) return;
    // 思路内容为空时跳过保存，避免触发后端 merged_content 非空校验（422）
    if (!content || !content.trim()) {
      return;
    }
    // 提交前刷新最新版本号，避免闭包旧值触发 409
    const fresh = await getFreshWorkspace();
    await outlineWorkspaceApi.saveMergedWritingIdea(projectId, {
      base_project_version: (fresh ?? workspace)?.project_version ?? 0,
      outline_id: modalNodeId,
      merged_content: content,
      ...(style ? { style } : {}),
      create_version: createVersion,
      // 候选态时只改当前选中那套目录的思路
      ...(activeCandidateId ? { candidate_id: activeCandidateId } : {}),
    });
    await loadWorkspace();
  };

  // 生成目录（弹窗内）
  const handleModalGenerateCatalog = async (
    ideaContent: string,
    instruction: string,
    style: string
  ): Promise<DurableOutlineNode[] | null> => {
    if (!projectId || !workspace) return null;
    // "保存思路并生成目录"：先保存思路到 bid_outline（不创建版本快照），
    // 再提交目录生成任务——worker 完成后会创建 BidOutlineVersion 快照。
    // createVersion=false 避免双重版本写入（save_tree 写一次 + worker 又写一次）。
    if (ideaContent && ideaContent !== modalInitialIdea) {
      await handleModalSaveIdea(ideaContent, undefined, false);
    }
    // 保存后重新拉取最新版本号再提交生成（防止闭包旧版本导致 409）
    const fresh = await getFreshWorkspace();
    const baseVersion = (fresh ?? workspace)?.project_version ?? 0;
    const baseOutlineVersion = (fresh ?? workspace)?.outline_version ?? 0;
    // 生成目录
    const response = await outlineWorkspaceApi.createOperation(projectId, {
      base_project_version: baseVersion,
      base_outline_version: baseOutlineVersion,
      operation_type: modalMode === 'ai_expand_catalog' ? 'expand' : 'optimize',
      target_ids: [modalNodeId],
      template_code: 'structure',
      instruction,
      style: style as any,
      idempotency_key: makeIdempotencyKey(modalMode, projectId, baseVersion, baseOutlineVersion, modalNodeId),
    });
    // 轮询等待任务完成
    // BidAIOperation.status：0=待处理, 10=进行中, 20=成功(READY), 30=已采用, 40=已放弃, 50=失败
    const taskId = response.data?.data?.task?.id;
    if (taskId) {
      const POLL_INTERVAL_MS = 2000;
      const MAX_POLLS = 90; // 最多 ~180 秒
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        const operationsResponse = await outlineWorkspaceApi.operations(projectId);
        const operations = operationsResponse.data?.data || [];
        const latestOperation = operations.find(op => op.task_id === taskId);
        if (!latestOperation) {
          continue;
        }
        // 20=成功(READY) → 终止轮询并返回目标章节子树
        if (latestOperation.status === 20) {
          const snapshot = latestOperation.result_snapshot as
            | { target_subtrees?: DurableOutlineNode[]; outline?: DurableOutlineNode[] }
            | undefined;
          const subtrees = snapshot?.target_subtrees;
          const fullOutline = snapshot?.outline;
          // target_subtrees 是后端正确裁剪好的目标节点（AI 只修改了 target_ids 对应的那些）
          // fullOutline 始终是 merge 后的完整目录树（包含所有章节），所以绝对不能用
          // fullOutline.length > subtrees.length 来判断——那永远是 true！
          // 正确做法：永远用 target_subtrees，拿到它的 children 作为扩写结果。
          if (subtrees && subtrees.length > 0) {
            const first = subtrees[0];
            return (first.children ?? []) as DurableOutlineNode[];
          }
          // 兜底：如果连 target_subtrees 都没有，才退回 fullOutline[0].children
          if (fullOutline && fullOutline.length > 0) {
            return (fullOutline[0].children ?? []) as DurableOutlineNode[];
          }
          return null;
        }
        // 50=失败 → 抛错让外层 catch 显示后端错误信息
        if (latestOperation.status === 50) {
          throw new Error(
            '目录生成失败（status=50），请稍后重试或查看「目录历史版本」'
          );
        }
        // 0/10（待处理/进行中）继续轮询
      }
    }
    return null;
  };

  // 重新生成章节编写思路（弹窗内）：调用 AI 从零生成，不依赖当前思路文本
  const handleModalGenerateIdea = async (): Promise<string> => {
    if (!projectId || !workspace || !modalNodeId || !modalNodeTitle) {
      throw new Error('缺少必要的节点信息');
    }
    const response = await outlineWorkspaceApi.generateWritingIdea(projectId, {
      node_id: modalNodeId,
      node_title: modalNodeTitle,
      instruction: '',
      style: candidateStyles[currentCandidateIndex] ?? 'general',
    });
    const generated = response.data?.data?.generated_idea || '';
    if (!generated) {
      throw new Error('生成结果为空');
    }
    return generated;
  };

  // 插入目录（弹窗内）：用后端最新 outline 替换目标节点的 children，
  // 不依赖 draft state（避免 draft 脏值或过时数据导致插入无效果）
  const handleModalInsertCatalog = async (catalog: DurableOutlineNode[]) => {
    if (!projectId) return;
    try {
      setMessage('正在插入目录...');
      setError('');
      // 1) 先拉取后端最新 workspace 拿到最新 outline 和 project_version
      const latest = await getFreshWorkspace();
      const baseWorkspace = latest ?? workspace;
      if (!baseWorkspace) {
        setError('工作区未初始化');
        return;
      }

      // 2) 构造新 outline
      // 注：后端 saveTree 会把 ai- 前缀 ID 规范化为点分编号（如 "ai-154-c1-8" → "8"），
      // 因此原 modalNodeId（候选快照的旧 AI ID）无法在 baseWorkspace 中匹配。
      // 一级章节的顺序在 AI 操作前后保持稳定，用 index 定位最可靠。
      let newOutline: DurableOutlineNode[];
      const wasFullOutline = modalFullOutlineRef.current;
      if (wasFullOutline) {
        newOutline = catalog;
      } else {
        // 局部替换：仅替换目标节点的 children
        const updateCatalog = (
          nodes: DurableOutlineNode[]
        ): DurableOutlineNode[] => {
          return nodes.map((node) => {
            if (node.id === modalNodeId) {
              return { ...node, children: catalog };
            }
            if (node.children) {
              return { ...node, children: updateCatalog(node.children) };
            }
            return node;
          });
        };
        if (
          modalNodeIndex >= 0
          && modalNodeIndex < baseWorkspace.outline.length
        ) {
          // 优先按 index 定位（最稳定，不受 ID 规范化影响）
          newOutline = baseWorkspace.outline.map((node, idx) =>
            idx === modalNodeIndex ? { ...node, children: catalog } : node
          );
        } else {
          // 兜底：按 modalNodeId 递归查找（处理非一级章节场景）
          newOutline = updateCatalog(baseWorkspace.outline);
        }
      }
      // 同步本地 draft，保证后续 saveDraft 与状态一致
      setDraft(newOutline);

      // 3) 校验稳定 ID
      if (collectNodeIds(newOutline).some((id) => !id.trim())) {
        setError('目录存在无效稳定 ID');
        return;
      }

      // 4) 传完整目录树（newOutline 是包含所有章节的全量树，只替换了目标章节的 children）
      //    后端 save_tree 会做全量 diff + 软删旧 children + 插入新 children。
      //    传 partial=true 会跳过软删，导致旧节点残留！
      const response = await outlineWorkspaceApi.save(projectId, {
        base_project_version: baseWorkspace.project_version,
        outline: toWritableOutline(newOutline),
        confirm_content_impact: true,
      });
      const next = response.data.data;
      // 不走 acceptServerState（它会 void loadWorkspace()，把 candidateTask
      // 重新拉回"上次 AI 生成的冻结快照"，导致 outlineData 仍显示老目录）。
      // 直接同步本地 state，并清掉 candidateTask 让 outlineData 退回 draft。
      setWorkspace(next);
      setDraft(cloneOutline(next.outline));
      setDirty(false);
      setSelectedIds(new Set());
      setCandidateTask(null);
      setMessage('目录已替换并保存');
      setError('');
    } catch (requestError) {
      setError(getErrorMessage(requestError, '插入目录失败'));
    }
  };

  // 恢复思路历史版本
  const handleRestoreIdeaVersion = (version: HistoryVersion) => {
    console.log('恢复思路版本:', version.title);
  };

  // 恢复目录历史版本
  const handleRestoreCatalogVersion = async (version: OutlineVersionRecord) => {
    if (!projectId) return;
    try {
      setMessage('正在恢复目录版本...');
      // 提交前刷新最新版本号，避免闭包旧值触发 409
      const fresh = await getFreshWorkspace();
      const baseVersion = (fresh ?? workspace)?.project_version ?? 0;
      await outlineWorkspaceApi.restore(projectId, version.version_no, {
        base_project_version: baseVersion,
        confirm_content_impact: true,
      });
      await loadWorkspace();
      setMessage(`已恢复目录版本 V${version.version_no}`);
    } catch (error) {
      setMessage(getErrorMessage(error, '恢复目录版本失败'));
    }
  };

  // 保存目录节点标题
  const handleSaveOutlineNodeTitle = async (nodeId: string) => {
    if (!editingOutlineNodeTitle.trim() || !projectId || !candidateTask) {
      setEditingOutlineNodeId(null);
      setEditingOutlineNodeTitle('');
      return;
    }
    try {
      setSavingOutlineNodeId(nodeId);
      const item = candidateTask.items?.[currentCandidateIndex];
      const candidateId = item?.content_plan?.candidate_id as string | undefined;
      if (!candidateId) {
        setError('无法获取候选方案ID');
        return;
      }
      await outlineWorkspaceApi.updateCandidateNodeTitle(
        projectId,
        candidateId,
        nodeId,
        editingOutlineNodeTitle.trim()
      );
      setMessage('标题已更新');
      // 更新本地数据：同时更新 draft 和 candidateTask 快照
      // （outlineData 优先用 candidateTask 快照，只 setDraft 不会让页面+下载生效）
      if (outlineData.length > 0) {
        const updateNode = (nodes: any[]): any[] => {
          return nodes.map((n: any) => {
            if (n.id === nodeId) {
              return { ...n, title: editingOutlineNodeTitle.trim() };
            }
            if (n.children) {
              return { ...n, children: updateNode(n.children) };
            }
            return n;
          });
        };
        const updatedData = updateNode(outlineData);
        // 1) 更新 draft（保存到 DB 后用）
        setDraft([...updatedData]);
        // 2) 同步更新 candidateTask 中当前 item 的 snapshot.outline
        //    让 outlineData 的 useMemo 能立即读到新标题（页面显示 + 下载都用它）
        if (candidateTask) {
          setCandidateTask((prev) => {
            if (!prev) return prev;
            const items = [...(prev.items ?? [])];
            const idx = currentCandidateIndex;
            if (idx >= 0 && idx < items.length) {
              const it = items[idx] as any;
              const snapshot = (it.result_snapshot as Record<string, any> | null) || {};
              items[idx] = {
                ...it,
                result_snapshot: { ...snapshot, outline: updatedData },
              };
            }
            return { ...prev, items };
          });
        }
      }
    } catch (error) {
      setError(getErrorMessage(error, '保存标题失败'));
    } finally {
      setSavingOutlineNodeId(null);
      setEditingOutlineNodeId(null);
      setEditingOutlineNodeTitle('');
    }
  };

  // 取消编辑目录节点标题
  const handleCancelEditOutlineNodeTitle = () => {
    setEditingOutlineNodeId(null);
    setEditingOutlineNodeTitle('');
  };

  // AI优化思路（调用后端 idea-optimize 同步接口）
  const handleAIOptimizeIdea = async (ideaId: string) => {
    if (!projectId || !workspace) return;
    try {
      // 找到当前 idea 的文本
      const currentIdea = ideasData.find((item: any) => item.id === ideaId);
      if (!currentIdea?.description) {
        setError('当前思路内容为空，无法优化');
        return;
      }
      setCurrentOperatingNodeId(ideaId);
      setMessage('正在AI优化思路...');
      const response = await outlineWorkspaceApi.optimizeIdea(projectId, {
        node_id: ideaId,
        current_idea: currentIdea.description,
        instruction: '优化当前章节的编写思路，使其更加清晰、具体、专业',
        style: candidateStyles[currentCandidateIndex] ?? 'general',
      });
      const optimizedIdea = response.data?.data?.optimized_idea;
      if (optimizedIdea) {
        // 将优化结果填充到编辑态，用户确认后保存
        setEditingIdeaId(ideaId);
        setEditingIdeaContent(optimizedIdea);
        setMessage('AI优化完成，请查看并确认保存');
      } else {
        setError('AI优化思路结果为空');
      }
    } catch (error) {
      setError(getErrorMessage(error, 'AI优化思路失败'));
    } finally {
      setCurrentOperatingNodeId(null);
    }
  };

  // ========== 右侧目录操作 ==========

  // AI扩写目录
  const handleAIExpandOutline = async (nodeId: string) => {
    if (!projectId || !workspace) return;
    try {
      setCurrentOperatingNodeId(nodeId);
      setMessage('正在AI扩写目录...');
      const response = await outlineWorkspaceApi.createOperation(
        projectId,
        {
          base_project_version: workspace.project_version,
          base_outline_version: workspace.outline_version,
          operation_type: 'expand',
          target_ids: [nodeId],
          template_code: 'structure',
          instruction: '以当前章节为根节点，扩写完整的子目录结构',
          style: candidateStyles[currentCandidateIndex] ?? 'general',
          idempotency_key: makeIdempotencyKey('expand', projectId, workspace.project_version, workspace.outline_version, nodeId),
        }
      );
      setOperationTask(response.data?.data?.task || null);
      setMessage('AI扩写目录任务已创建，请查看结果');
    } catch (error) {
      setError(getErrorMessage(error, 'AI扩写目录失败'));
    } finally {
      setCurrentOperatingNodeId(null);
    }
  };

  // AI优化目录
  const handleAIOptimizeOutline = async (nodeId: string) => {
    if (!projectId || !workspace) return;
    try {
      setCurrentOperatingNodeId(nodeId);
      setMessage('正在AI优化目录...');
      const response = await outlineWorkspaceApi.createOperation(
        projectId,
        {
          base_project_version: workspace.project_version,
          base_outline_version: workspace.outline_version,
          operation_type: 'optimize',
          target_ids: [nodeId],
          template_code: 'structure',
          instruction: '对当前章节的目录结构和标题进行优化',
          style: candidateStyles[currentCandidateIndex] ?? 'general',
          idempotency_key: makeIdempotencyKey('optimize', projectId, workspace.project_version, workspace.outline_version, nodeId),
        }
      );
      setOperationTask(response.data?.data?.task || null);
      setMessage('AI优化目录任务已创建，请查看结果');
    } catch (error) {
      setError(getErrorMessage(error, 'AI优化目录失败'));
    } finally {
      setCurrentOperatingNodeId(null);
    }
  };

  const runWithImpactConfirmation = async (
    action: (confirmed: boolean) => Promise<OutlineWorkspaceState>
  ) => {
    try {
      return await action(false);
    } catch (requestError) {
      if (
        !isContentImpactConflict(requestError)
        || !window.confirm(
          `${getErrorMessage(requestError)}。继续操作可能使已有正文节点失效，是否确认继续？`
        )
      ) {
        throw requestError;
      }
      return action(true);
    }
  };

  const acceptServerState = (
    next: OutlineWorkspaceState,
    successMessage: string
  ) => {
    setWorkspace(next);
    setDraft(cloneOutline(next.outline));
    setDirty(false);
    setSelectedIds(new Set());
    setMessage(successMessage);
    setError('');
    void loadWorkspace();
  };

  const saveDraft = async () => {
    if (!projectId || !workspace) return;
    if (draft.length === 0) {
      setError('目录不能为空');
      return;
    }
    if (collectNodeIds(draft).some((id) => !id.trim())) {
      setError('目录存在无效稳定 ID');
      return;
    }
    try {
      setActionKey('save');
      setMessage('');
      setError('');
      const next = await runWithImpactConfirmation(async (confirmed) => {
        const response = await outlineWorkspaceApi.save(projectId, {
          base_project_version: workspace.project_version,
          outline: toWritableOutline(draft),
          confirm_content_impact: confirmed,
        });
        return response.data.data;
      });
      acceptServerState(next, '目录已保存为未确认的新版本');
    } catch (requestError) {
      setError(getErrorMessage(requestError, '目录保存失败'));
    } finally {
      setActionKey('');
    }
  };

  const confirmOutline = async () => {
    if (!projectId || !workspace || workspace.outline_version <= 0) return;
    if (dirty) {
      setError('当前有未保存修改，请先保存目录');
      return;
    }
    try {
      setActionKey('confirm');
      setMessage('');
      setError('');
      const response = await outlineWorkspaceApi.confirm(projectId, {
        base_project_version: workspace.project_version,
        outline_version: workspace.outline_version,
      });
      acceptServerState(response.data.data, '当前目录版本已确认');
      onConfirmed?.();
    } catch (requestError) {
      setError(getErrorMessage(requestError, '目录确认失败'));
    } finally {
      setActionKey('');
    }
  };

  const createCandidates = async (opts?: { singleCandidateId?: string; style?: string }) => {
    if (!projectId || !workspace) return;
    if (!analysisConfirmed || analysisVersion <= 0) {
      setError('请先确认资料解析结果');
      return;
    }
    if (dirty) {
      setError('当前目录有未保存修改，请先保存或刷新');
      return;
    }
    try {
      setActionKey('candidates');
      setMessage('');
      setError('');
      setCandidatePhase('ideas');
      // 优先使用传入的 style，其次用当前候选的风格，最后用全局风格
      const effectiveStyle = opts?.style
        ?? candidateStyles[currentCandidateIndex]
        ?? candidateStyle;
      const response = await outlineWorkspaceApi.createCandidateTask(
        projectId,
        {
          base_project_version: workspace.project_version,
          base_analysis_version: analysisVersion,
          mode: candidateMode,
          instruction: candidateInstruction.trim(),
          style: effectiveStyle,
          idempotency_key: makeIdempotencyKey(
            'outline-candidates',
            projectId,
            workspace.project_version,
            analysisVersion,
            effectiveStyle
          ),
          ...(opts?.singleCandidateId
            ? { single_candidate_id: opts.singleCandidateId }
            : {}),
        }
      );
      const newTask = response.data.data;
      if (opts?.singleCandidateId && candidateTask) {
        // 单套重生成：用新任务的 item 替换当前任务中对应候选的 item。
        // 关键：task 元信息（id / status / request_snapshot）整体更新为新 task，
        // 这样 useTaskSubscription 能订阅到新 task 的进度事件；
        // 但 items 只覆盖 target_id 命中的条目，保留其他两套的目录。
        const newItems = (newTask?.items ?? []) as Array<any>;
        setCandidateTask((prev) => {
          if (!prev) return newTask as any;
          const prevItems = (prev.items ?? []) as Array<any>;
          const prevMap = new Map(
            prevItems.map((it) => [it.target_id, it])
          );
          // 只覆盖目标 candidate 的那一套，其他两套保留旧的（已 SUCCESS）
          // 后端暂未实现 single_candidate 过滤，会返回全套 item，
          // 所以这里必须手动只替换目标套，否则其他两套也会被新 PENDING item 覆盖
          for (const it of newItems) {
            if (it.target_id === opts.singleCandidateId) {
              prevMap.set(it.target_id, it);
            }
          }
          // 保持 prev 的排序：prev 原有顺序 + 新增追加到末尾
          const seen = new Set<string>();
          const mergedOrdered: Array<any> = [];
          for (const it of prevItems) {
            const key = it.target_id;
            if (!seen.has(key) && prevMap.has(key)) {
              mergedOrdered.push(prevMap.get(key)!);
              seen.add(key);
            }
          }
          for (const it of newItems) {
            const key = it.target_id;
            if (!seen.has(key) && prevMap.has(key)) {
              mergedOrdered.push(prevMap.get(key)!);
              seen.add(key);
            }
          }
          // 用新 task 的元信息 + 合并后的 items
          return { ...(newTask as any), items: mergedOrdered };
        });
      } else {
        // 全量生成：直接替换
        setCandidateTask(newTask);
        // 初始化三套各自的风格（从后端 profile 读取）
        const items = (newTask?.items ?? []) as Array<{
          target_id?: string;
          content_plan?: { style?: CandidateStyle };
        }>;
        const next: Record<number, CandidateStyle> = {
          0: 'general',
          1: 'general',
          2: 'general',
        };
        items.forEach((it, i) => {
          if (it.content_plan?.style) next[i] = it.content_plan.style;
        });
        setCandidateStyles(next);
      }
      // 任务已创建，不再提示等待消息，因为用户会立即看到结果
    } catch (requestError) {
      setError(getErrorMessage(requestError, '编写思路生成任务创建失败'));
    } finally {
      setActionKey('');
    }
  };
  // 更新ref以便自动触发useEffect调用
  createCandidatesRef.current = createCandidates;

  // 将目录树递归转为 Word 导出所需的 outline 结构（OutlineItem 格式）
  const buildExportOutline = (nodes: DurableOutlineNode[]): OutlineItem[] => {
    return nodes.map((n) => ({
      id: String(n.id),
      title: n.title,
      description: n.description || '',
      children: n.children && n.children.length > 0
        ? buildExportOutline(n.children)
        : undefined,
    }));
  };

  // 将目录树递归转为纯文本（缩进表示层级），用于 txt 下载
  const outlineToText = (nodes: DurableOutlineNode[], depth = 0): string => {
    let text = '';
    const indent = '  '.repeat(depth);
    for (const n of nodes || []) {
      // 跳过空行/空标题节点
      if (!n.title || !n.title.trim()) continue;
      text += `${indent}${n.title.trim()}\n`;
      if (n.children && n.children.length > 0) {
        text += outlineToText(n.children, depth + 1);
      }
    }
    return text;
  };

  // 下载当前选中候选目录（支持 txt / docx 两种格式）
  const handleDownloadOutline = async (format: 'txt' | 'docx') => {
    setShowDownloadMenu(false);
    if (!outlineData || outlineData.length === 0) {
      setError('暂无可下载的目录，请先生成目录');
      return;
    }
    const baseName = `目录${currentCandidateIndex + 1}`;
    try {
      if (format === 'txt') {
        const text = outlineToText(outlineData);
        const blob = new Blob(['\uFEFF' + text], {
          type: 'text/plain;charset=utf-8',
        });
        saveAs(blob, `${baseName}.txt`);
        setMessage('目录已下载');
      } else {
        const response = await documentApi.exportWord({
          project_name: baseName,
          outline: buildExportOutline(outlineData),
        });
        const blob = await response.blob();
        saveAs(blob, `${baseName}.docx`);
        setMessage('目录已下载');
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError, '目录下载失败'));
    }
  };

  // 进入页面后自动调用：一次性生成编写思路并直接回填一级节点（不走多候选）
  const applyWritingIdeas = async () => {
    if (!projectId || !workspace) return;
    if (!analysisConfirmed || analysisVersion <= 0) {
      setError('请先确认资料解析结果');
      return;
    }
    try {
      setActionKey('apply-ideas');
      setMessage('');
      setError('');
      setMessage('正在生成编写思路，请稍候…');
      const response = await outlineWorkspaceApi.applyWritingIdeas(projectId);
      acceptServerState(response.data.data, '编写思路已生成');
      // 思路已自动生成并回填，切到第二步「生成目录」
      setCandidatePhase('outline');
      setMessage('');
    } catch (requestError) {
      setError(getErrorMessage(requestError, '编写思路生成失败'));
    } finally {
      setActionKey('');
    }
  };
  // 更新ref以便自动触发useEffect调用
  applyWritingIdeasRef.current = applyWritingIdeas;

  const generateOutlineFromIdeas = async () => {
    if (!projectId || !workspace || !candidateTask) return;
    try {
      setActionKey('generate-outline');
      setMessage('');
      setError('');
      setCandidatePhase('outline');
      const response = await outlineWorkspaceApi.createCandidateTask(
        projectId,
        {
          base_project_version: workspace.project_version,
          base_analysis_version: analysisVersion,
          mode: candidateMode,
          instruction: candidateInstruction.trim(),
          style: candidateStyle,
          idempotency_key: makeIdempotencyKey(
            'outline-from-ideas',
            projectId,
            workspace.project_version,
            analysisVersion,
            candidateStyle
          ),
        }
      );
      setCandidateTask(response.data.data);
      setMessage('根据思路生成目录任务已创建');
    } catch (requestError) {
      setError(getErrorMessage(requestError, '目录生成任务创建失败'));
    } finally {
      setActionKey('');
    }
  };

  const applyCandidate = async (item: PersistedTaskItem) => {
    if (!projectId || !workspace || !candidateTask) return;
    try {
      setActionKey(`candidate-${item.target_id}`);
      setMessage('');
      setError('');
      const next = await runWithImpactConfirmation(async (confirmed) => {
        const response = await outlineWorkspaceApi.applyCandidate(
          projectId,
          item.target_id,
          {
            task_id: candidateTask.id,
            base_project_version: workspace.project_version,
            base_analysis_version: analysisVersion,
            confirm_content_impact: confirmed,
          }
        );
        return response.data.data;
      });
      acceptServerState(next, '目录候选已采用，请检查并确认新版本');
    } catch (requestError) {
      setError(getErrorMessage(requestError, '目录候选采用失败'));
    } finally {
      setActionKey('');
    }
  };

  const createOperation = async () => {
    if (!projectId || !workspace) return;
    if (dirty) {
      setError('当前目录有未保存修改，请先保存后再执行智能操作');
      return;
    }
    if (selectedIds.size === 0) {
      setError('请先勾选要处理的目录节点');
      return;
    }
    if (
      operationType === 'manual_idea'
      && !operationInstruction.trim()
    ) {
      setError('编写思路不能为空');
      return;
    }
    try {
      setActionKey('operation');
      setMessage('');
      setError('');
      const response = await outlineWorkspaceApi.createOperation(
        projectId,
        {
          base_project_version: workspace.project_version,
          base_outline_version: workspace.outline_version,
          operation_type: operationType,
          target_ids: Array.from(selectedIds),
          template_code: operationTemplate,
          instruction: operationInstruction.trim(),
          style: operationStyle,
          idempotency_key: makeIdempotencyKey(
            `outline-${operationType}`,
            projectId,
            workspace.project_version,
            workspace.outline_version
          ),
        }
      );
      setOperationTask(response.data.data.task);
      setOperations((current) => [
        response.data.data.operation,
        ...current.filter(
          (item) => item.id !== response.data.data.operation.id
        ),
      ]);
      setMessage(
        operationType === 'manual_idea'
          ? '编写思路候选已生成，采用前不会覆盖当前目录'
          : '目录智能操作任务已创建'
      );
      if (!ACTIVE_TASK_STATUSES.has(response.data.data.task.status)) {
        void loadWorkspace();
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError, '目录智能操作创建失败'));
    } finally {
      setActionKey('');
    }
  };

  const applyOperation = async (operation: OutlineAIOperation) => {
    if (!projectId || !workspace) return;
    try {
      setActionKey(`operation-${operation.id}`);
      setMessage('');
      setError('');
      const next = await runWithImpactConfirmation(async (confirmed) => {
        const response = await outlineWorkspaceApi.applyOperation(
          projectId,
          operation.id,
          {
            base_project_version: workspace.project_version,
            base_outline_version: workspace.outline_version,
            confirm_content_impact: confirmed,
          }
        );
        return response.data.data;
      });
      acceptServerState(next, '智能操作候选已采用，请检查并确认新版本');
    } catch (requestError) {
      setError(getErrorMessage(requestError, '智能操作候选采用失败'));
    } finally {
      setActionKey('');
    }
  };

  const restoreVersion = async (version: OutlineVersionRecord) => {
    if (!projectId || !workspace) return;
    if (
      !window.confirm(
        `确定将历史版本 V${version.version_no} 恢复为新的未确认版本吗？`
      )
    ) {
      return;
    }
    try {
      setActionKey(`restore-${version.version_no}`);
      setMessage('');
      setError('');
      const next = await runWithImpactConfirmation(async (confirmed) => {
        const response = await outlineWorkspaceApi.restore(
          projectId,
          version.version_no,
          {
            base_project_version: workspace.project_version,
            confirm_content_impact: confirmed,
          }
        );
        return response.data.data;
      });
      acceptServerState(next, `历史版本 V${version.version_no} 已恢复`);
    } catch (requestError) {
      setError(getErrorMessage(requestError, '目录历史版本恢复失败'));
    } finally {
      setActionKey('');
    }
  };

  const controlTask = async (
    task: PersistedTaskSnapshot,
    action: 'cancel' | 'retry'
  ) => {
    try {
      setActionKey(`${action}-${task.id}`);
      setError('');
      const response = action === 'cancel'
        ? await taskApi.cancel(task.id)
        : await taskApi.retryFailedItems(task.id);
      if (candidateTask?.id === task.id) {
        setCandidateTask(response.data.data);
      }
      if (operationTask?.id === task.id) {
        setOperationTask(response.data.data);
      }
      setMessage(action === 'cancel' ? '已提交取消请求' : '失败项已重新排队');
    } catch (requestError) {
      setError(getErrorMessage(requestError, '任务操作失败'));
    } finally {
      setActionKey('');
    }
  };

  if (!projectId) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-700">
        请先保存项目配置，再生成标书目录。
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-28 animate-pulse rounded-2xl bg-white" />
        <div className="h-[520px] animate-pulse rounded-2xl bg-white" />
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {error || '目录工作台加载失败'}
        <button
          type="button"
          onClick={() => void loadWorkspace()}
          className="ml-3 font-medium underline"
        >
          重新加载
        </button>
      </div>
    );
  }

  const candidateBaseProjectVersion = Number(
    candidateTask?.request_snapshot?.base_project_version ?? -1
  );
  const candidateBaseAnalysisVersion = Number(
    candidateTask?.request_snapshot?.base_analysis_version ?? -1
  );
  const candidateStale = Boolean(
    candidateTask
    && (
      candidateBaseProjectVersion !== workspace.project_version
      || candidateBaseAnalysisVersion !== analysisVersion
    )
  );

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_8px_30px_rgba(31,52,86,0.04)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              版本化标书目录
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              编辑保留稳定目录 ID，保存、采用和恢复都会生成可追溯的新版本。
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-slate-100 px-3 py-1.5 text-slate-600">
              项目版本 V{workspace.project_version}
            </span>
            <span className="rounded-full bg-blue-50 px-3 py-1.5 text-blue-700">
              目录版本 V{workspace.outline_version}
            </span>
            <span className={`rounded-full px-3 py-1.5 ${
              workspace.confirmed
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-amber-50 text-amber-700'
            }`}>
              {workspace.confirmed ? '已确认' : '未确认'}
            </span>
            {dirty && (
              <span className="rounded-full bg-orange-50 px-3 py-1.5 text-orange-700">
                有未保存修改
              </span>
            )}
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-4">
          <div className="rounded-xl bg-slate-50 px-4 py-3">
            <div className="text-xs text-slate-400">目录节点</div>
            <div className="mt-1 text-lg font-semibold text-slate-800">
              {outlineStats.nodes}
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 px-4 py-3">
            <div className="text-xs text-slate-400">叶子章节</div>
            <div className="mt-1 text-lg font-semibold text-slate-800">
              {outlineStats.leaves}
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 px-4 py-3">
            <div className="text-xs text-slate-400">资料版本</div>
            <div className="mt-1 text-lg font-semibold text-slate-800">
              V{analysisVersion}
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 px-4 py-3">
            <div className="text-xs text-slate-400">已选智能操作节点</div>
            <div className="mt-1 text-lg font-semibold text-slate-800">
              {selectedIds.size}
            </div>
          </div>
        </div>
      </div>

      {(message || error) && (
        <div className={`rounded-xl px-4 py-3 text-sm ${
          error
            ? 'bg-red-50 text-red-700'
            : 'bg-emerald-50 text-emerald-700'
        }`}>
          {error || message}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        {/* 左侧：编写思路面板 */}
        <div className="rounded-2xl border border-slate-200 bg-white flex flex-col" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {/* 4个Tab切换 */}
          <div className="flex items-center gap-1 border-b border-slate-200 px-4 pt-3">
            {[
              { key: 'ideas', label: '目录编写思路' },
              { key: 'tender', label: '招标文件' },
              { key: 'scoring', label: '评分点' },
              { key: 'overview', label: '项目概述' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                  activeTab === tab.key
                    ? 'text-slate-800 border-b-2 border-[var(--bid-color-primary)] -mb-px'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
                {tab.key === 'ideas' && (
                  <span className="ml-1 text-[10px] text-purple-500">✦</span>
                )}
              </button>
            ))}
          </div>

          {/* Tab内容区：可滚动 */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Tab=目录编写思路 时显示章节思路卡片 */}
            {activeTab === 'ideas' && (
              <div className="space-y-4">
                {ideasData.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 px-6 py-14 text-center">
                    <div className="text-sm text-slate-500">当前还没有目录</div>
                    <div className="mt-1 text-xs text-slate-400">
                      请先生成编写思路
                    </div>
                  </div>
                ) : (
                  ideasData.map((idea: any, i: number) => {
                    const chapterNames = ['第一章','第二章','第三章','第四章','第五章','第六章','第七章','第八章','第九章','第十章'];
                    const isEditing = editingIdeaId === idea.id;
                    const isOperating = currentOperatingNodeId === idea.id;
                    return (
                      <div key={idea.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-sm transition-shadow">
                        {/* 章节标题 */}
                        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                          <div className="font-semibold text-slate-800">
                            {chapterNames[i] || `第${i+1}章`} {idea.title}
                          </div>
                        </div>
                        
                        {/* 编写思路内容 */}
                        <div className="p-4">
                          {/* 编写思路说明 */}
                          <div className="mb-3 flex items-start gap-1.5 text-xs text-purple-600">
                            <span>📝</span>
                            <span>
                              <span className="font-medium">编写思路</span>
                              {' '}编写思路决定章节目录的编写方向和页数，请点击目录优化修改
                            </span>
                          </div>
                          
                          {/* 具体的编写思路描述 - 支持编辑模式 */}
                          {isEditing ? (
                            <div>
                              <textarea
                                value={editingIdeaContent}
                                onChange={(e) => setEditingIdeaContent(e.target.value)}
                                className="w-full min-h-[120px] rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="请输入编写思路内容..."
                              />
                              <div className="mt-2 flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={handleCancelEditIdea}
                                  className="px-3 py-1 text-xs rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50"
                                >
                                  取消
                                </button>
                                <button
                                  type="button"
                                  onClick={handleSaveEditedIdea}
                                  className="px-3 py-1 text-xs rounded-md bg-blue-500 text-white hover:bg-blue-600"
                                >
                                  保存
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed pl-1">
                              {idea.description || '暂无编写思路，请先生成。'}
                            </div>
                          )}
                        </div>
                        
                        {/* 底部按钮：设置编写风格 + 手动编辑思路 + AI优化思路 */}
                        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50/30">
                          <button
                            type="button"
                            onClick={async () => {
                              // 找到对应的节点
                              const node = draft.find((n: any) => n.id === idea.id);
                              const currentCatalog = node?.children || [];
                              await openModal('style_setting', idea.id, idea.title, idea.description || '', currentCatalog);
                            }}
                            className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200"
                          >
                            ⚙ 设置编写风格
                          </button>
                          {!isEditing && (
                            <button
                              type="button"
                              onClick={async () => {
                                // 找到对应的节点
                                const node = draft.find((n: any) => n.id === idea.id);
                                const currentCatalog = node?.children || [];
                                await openModal('manual_idea', idea.id, idea.title, idea.description || '', currentCatalog);
                              }}
                              className="inline-flex items-center gap-1.5 rounded-md bg-[#3b82f6] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#2563eb] shadow-sm"
                            >
                              📝 手动编辑思路
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={async () => {
                              // 找到对应的节点
                              const node = draft.find((n: any) => n.id === idea.id);
                              const currentCatalog = node?.children || [];
                              await openModal('ai_optimize_idea', idea.id, idea.title, idea.description || '', currentCatalog);
                            }}
                            disabled={isOperating}
                            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-purple-500 to-indigo-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:from-purple-600 hover:to-indigo-700 disabled:opacity-50"
                          >
                            ✨ {isOperating ? '优化中...' : 'AI优化思路'}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Tab=招标文件 */}
            {activeTab === 'tender' && (
              <div className="rounded-xl bg-slate-50 p-4">
                <h4 className="mb-2 text-sm font-medium text-slate-700">招标文件</h4>
                <div className="text-xs text-slate-500 whitespace-pre-wrap max-h-[500px] overflow-auto leading-relaxed">
                  {analysisData?.overview || '暂无招标文件内容，请先在招标文件解读页上传文件或粘贴内容。'}
                </div>
              </div>
            )}

            {/* Tab=评分点 */}
            {activeTab === 'scoring' && (
              <div className="rounded-xl bg-slate-50 p-4">
                <h4 className="mb-2 text-sm font-medium text-slate-700">评分点</h4>
                <div className="text-xs text-slate-500 whitespace-pre-wrap max-h-[500px] overflow-auto leading-relaxed">
                  {workspace?.requirements || '暂无评分点内容，请先在招标文件解读页上传文件或粘贴内容。'}
                </div>
              </div>
            )}

            {/* Tab=项目概述 */}
            {activeTab === 'overview' && (
              <div className="rounded-xl bg-slate-50 p-4">
                <h4 className="mb-2 text-sm font-medium text-slate-700">项目概述</h4>
                <div className="text-xs text-slate-500 whitespace-pre-wrap max-h-[500px] overflow-auto leading-relaxed">
                  {workspace?.overview || '暂无项目概述内容。'}
                </div>
              </div>
            )}
          </div>

          {/* 左侧底部：操作按钮区 */}
          {activeTab === 'ideas' && (
            <div className="border-t border-slate-200 px-4 py-4 flex items-center justify-center gap-3">
              {/* 上一步按钮 */}
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#e5e7eb] px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-[#d1d5db]"
              >
                <span>〈</span>
                上一步
                <span>〉</span>
              </button>
              
              {/* 生成目录按钮（中间） */}
              <button
                type="button"
                disabled={
                  candidateActive
                  || !analysisConfirmed
                  || dirty
                  || Boolean(actionKey)
                  || draft.length === 0
                }
                onClick={() => createCandidates()}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:from-slate-300 disabled:to-slate-300 shadow-sm hover:from-indigo-600 hover:to-purple-700"
              >
                <SparklesIcon className="h-4 w-4" />
                {actionKey === 'candidates' ? '生成中...' : '✨ 生成目录'}
              </button>
              
              {/* 编写正文按钮 */}
              <button
                type="button"
                disabled={
                  dirty
                  || workspace.confirmed
                  || workspace.outline_version <= 0
                  || Boolean(actionKey)
                }
                onClick={confirmOutline}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--bid-color-primary)] px-6 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300 shadow-sm hover:opacity-90"
              >
                <CheckCircleIcon className="h-4 w-4" />
                {actionKey === 'confirm' ? '确认中...' : '编写正文'}
              </button>
            </div>
          )}
        </div>

        {/* 右侧：目录三选面板 */}
        <div className="rounded-2xl border border-slate-200 bg-white flex flex-col" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {/* 顶部标题栏 */}
          <div className="px-5 pt-4 pb-3 border-b border-slate-200">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-base font-semibold text-slate-800 mb-1">目录三选一</div>
                <div className="text-xs text-slate-500">目录间可相互复制修改，生成最好的目录</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <button
                    type="button"
                    disabled={isCandidateGenerating(currentCandidateIndex)}
                    onClick={() => setShowStyleSelector(!showStyleSelector)}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                      isCandidateGenerating(currentCandidateIndex)
                        ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-600'
                    }`}
                  >
                    {isCandidateGenerating(currentCandidateIndex) ? (
                      // 正在生成 → loading 圆圈
                      <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                      </svg>
                    ) : (
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round"/>
                      </svg>
                    )}
                    {isCandidateGenerating(currentCandidateIndex)
                      ? '生成中，无法切换'
                      : '选择风格重新生成'}
                  </button>
                  {showStyleSelector && (
                    <div className="absolute right-0 top-full z-10 mt-2 w-48 rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
                      <div className="mb-2 text-xs font-medium text-slate-500">选择风格</div>
                      {(styleTemplates.length > 0
                        ? styleTemplates.slice(0, 3)
                        : ([
                            { value: 'general', label: '通用风格' },
                            { value: 'professional', label: '专业风格' },
                            { value: 'concise', label: '简洁风格' },
                          ] as TemplateItem[])
                      ).map((tpl) => (
                        <button
                          key={tpl.value}
                          type="button"
                          onClick={async () => {
                            // 正在生成时不允许切换风格
                            if (isCandidateGenerating(currentCandidateIndex)) {
                              setShowStyleSelector(false);
                              setMessage('当前目录正在重新生成中，请稍后再切换风格');
                              return;
                            }
                            const pickedStyle = tpl.value as CandidateStyle;
                            // 仅更新当前选中那套目录的风格，并重生成当前那套
                            setCandidateStyles((prev) => ({
                              ...prev,
                              [currentCandidateIndex]: pickedStyle,
                            }));
                            setCandidateStyle(pickedStyle);
                            setShowStyleSelector(false);
                            if (projectId && candidateTask) {
                              const currentItem = (candidateTask.items as Array<{ target_id?: string }> | undefined)?.[currentCandidateIndex];
                              const candidateId = currentItem?.target_id;
                              // 持久化保存当前那套目录的风格到项目候选配置
                              if (candidateId) {
                                try {
                                  await outlineWorkspaceApi.updateProjectCandidateProfile(
                                    projectId,
                                    candidateId,
                                    { style: tpl.value }
                                  );
                                } catch {
                                  /* 忽略落库失败，前端已更新 */
                                }
                              }
                              await createCandidates(
                                candidateId
                                  ? { singleCandidateId: candidateId, style: pickedStyle }
                                  : { style: pickedStyle }
                              );
                            }
                          }}
                          className={`w-full rounded px-2 py-1.5 text-left text-xs transition-colors ${
                            candidateStyles[currentCandidateIndex] === tpl.value
                              ? 'bg-indigo-50 text-indigo-700'
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {tpl.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 hover:border-indigo-300 hover:text-indigo-600"
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeLinecap="round"/>
                      <polyline points="7 10 12 15 17 10" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="12" y1="15" x2="12" y2="3" strokeLinecap="round"/>
                    </svg>
                    下载目录
                  </button>
                  {showDownloadMenu && (
                    <div className="absolute right-0 top-full z-10 mt-2 w-44 rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
                      <div className="mb-2 text-xs font-medium text-slate-500">下载格式</div>
                      <button
                        type="button"
                        onClick={async () => {
                          await handleDownloadOutline('txt');
                        }}
                        className="w-full rounded px-2 py-1.5 text-left text-xs text-slate-600 hover:bg-slate-50 hover:text-indigo-600"
                      >
                        TXT 文本
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          await handleDownloadOutline('docx');
                        }}
                        className="w-full rounded px-2 py-1.5 text-left text-xs text-slate-600 hover:bg-slate-50 hover:text-indigo-600"
                      >
                        Word (docx)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 候选方案三选一Tab */}
            <div className="mt-4 flex items-center gap-1 bg-slate-100/50 rounded-lg p-1">
              {[1, 2, 3].map((idx) => {
                const i = idx - 1;
                const generating = isCandidateGenerating(i);
                return (
                  <button
                    key={idx}
                    type="button"
                    className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-all flex items-center justify-center gap-1.5 ${
                      currentCandidateIndex === i
                        ? 'bg-white shadow-sm text-slate-800 ring-1 ring-slate-200'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    onClick={() => {
                      setCurrentCandidateIndex(i);
                      setCandidateStyle(candidateStyles[i] ?? 'general');
                    }}
                  >
                    目录{idx}（{
                      styleTemplates.find(t => t.value === candidateStyles[i])?.label
                      || STYLE_LABELS[candidateStyles[i] as keyof typeof STYLE_LABELS]
                      || '通用'
                    }）
                    {generating ? (
                      // 正在生成 → loading 圆圈
                      <svg className="w-3.5 h-3.5 text-indigo-500 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                      </svg>
                    ) : (
                      // 已完成 → ✓
                      <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-[9px]">✓</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* 页数进度条 */}
            <div className="mt-4 flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-blue-600">196</span>
                <span className="text-xs text-slate-400">/</span>
                <span className="text-xs text-slate-500">200页</span>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-600">
                ✓ 已匹配
              </span>
              <div className="flex-1 relative">
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full" style={{ width: '98%' }} />
                </div>
                <div className="absolute -bottom-5 left-0 text-[10px] text-slate-400">32</div>
                <div className="absolute -bottom-5 right-0 text-[10px] text-slate-400">1032</div>
                <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] text-slate-400">532</div>
                <div className="absolute top-1/2 left-[98%] -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 border-blue-500 shadow-sm cursor-pointer" />
              </div>
              <button
                type="button"
                className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-600 hover:bg-blue-100"
              >
                按目标分配
              </button>
            </div>
          </div>

          {/* 目录内容区：可滚动 - 使用outlineData显示完整多级目录 */}
          <div className="flex-1 overflow-y-auto p-4 mt-5">
            <div className="space-y-4">
              {outlineData.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 px-6 py-14 text-center">
                  <div className="text-sm text-slate-500">暂无目录内容</div>
                  <div className="mt-1 text-xs text-slate-400">请先生成目录候选</div>
                </div>
              ) : (
                outlineData.map((node: any, i: number) => {
                  const totalWords = outlineData.reduce((sum: number, n: any) => sum + (n.expected_word_count || 0), 0);
                  const nodeWords = node.expected_word_count || 50000;
                  const totalPagesEstimate = totalWords > 0 ? Math.ceil(totalWords / 800) : 50;
                  const recommendedPages = nodeWords > 0 ? Math.max(1, Math.round((nodeWords / (totalWords || 1)) * totalPagesEstimate)) : 62;
                  const minPages = Math.max(5, Math.round(recommendedPages * 0.3));
                  const maxPages = Math.round(recommendedPages * 1.7);
                  
                  const CN_NUMS = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十'];
                  const getSectionLabel = (lvl: number, idx: number): string => {
                    if (lvl === 0) return `第${CN_NUMS[idx] ?? idx + 1}节 `;
                    if (lvl === 1) return `（${CN_NUMS[idx] ?? idx + 1}）`;
                    return `${idx + 1}. `;
                  };
                  // 递归渲染子目录的函数
                  const renderChildren = (children: any[], level: number): React.ReactElement => {
                    if (!children || children.length === 0) return <></>;
                    // 每级缩进 16px，支持任意深度（Tailwind 预定义类间隔不均匀，用 style 更灵活）
                    const indentStyle = { paddingLeft: `${level * 16}px` };
                    return (
                      <div className="space-y-1 text-xs text-slate-600" style={indentStyle}>
                        {children.map((child: any, ci: number) => (
                          <div key={child.id} className="space-y-1">
                            {editingOutlineNodeId === child.id ? (
                              <div className="flex items-center gap-1">
                                <span>{getSectionLabel(level, ci)}</span>
                                <input
                                  type="text"
                                  value={editingOutlineNodeTitle}
                                  onChange={(e) => setEditingOutlineNodeTitle(e.target.value)}
                                  onBlur={() => handleSaveOutlineNodeTitle(child.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleSaveOutlineNodeTitle(child.id);
                                    } else if (e.key === 'Escape') {
                                      handleCancelEditOutlineNodeTitle();
                                    }
                                  }}
                                  className="flex-1 rounded border border-blue-300 bg-white px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                                  autoFocus
                                />
                                {savingOutlineNodeId === child.id && (
                                  <span className="text-[10px] text-blue-500">保存中...</span>
                                )}
                              </div>
                            ) : (
                              <div
                                className={`${level === 0 ? 'font-medium text-slate-700' : ''} cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 py-0.5 transition-colors group flex items-center justify-between gap-1`}
                                onClick={() => {
                                  setEditingOutlineNodeId(child.id);
                                  setEditingOutlineNodeTitle(child.title);
                                }}
                                title="点击编辑标题"
                              >
                                <span className="truncate">
                                  {getSectionLabel(level, ci)}
                                  {child.title}
                                </span>
                                <span className="flex items-center gap-0.5 shrink-0">
                                  {/* 上移按钮 */}
                                  {ci > 0 && (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={(e) => { e.stopPropagation(); moveNode(child.id, -1); }}
                                      className="text-[11px] text-slate-400 hover:text-blue-600 px-0.5 rounded"
                                      title="上移"
                                    >↑</span>
                                  )}
                                  {/* 下移按钮 */}
                                  {ci < children.length - 1 && (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={(e) => { e.stopPropagation(); moveNode(child.id, 1); }}
                                      className="text-[11px] text-slate-400 hover:text-blue-600 px-0.5 rounded"
                                      title="下移"
                                    >↓</span>
                                  )}
                                  {/* 添加子节点 */}
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => { e.stopPropagation(); addChild(child.id); }}
                                    className="text-[11px] text-slate-400 hover:text-green-600 px-0.5 rounded"
                                    title="添加子节点"
                                  >＋</span>
                                  {/* 编辑按钮 */}
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingOutlineNodeId(child.id);
                                      setEditingOutlineNodeTitle(child.title);
                                    }}
                                    className="text-[11px] text-slate-400 hover:text-blue-600 px-0.5 rounded"
                                    title="编辑标题"
                                  >✎</span>
                                  {/* 删除按钮 */}
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeNode(child.id, child.title);
                                    }}
                                    className="text-[11px] text-red-400 hover:text-red-600 px-0.5 rounded"
                                    title="删除此节点及其子目录"
                                  >🗑</span>
                                </span>
                              </div>
                            )}
                            {renderChildren(child.children || [], level + 1)}
                          </div>
                        ))}
                      </div>
                    );
                  };
                  
                  return (
                    <div key={node.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                      {/* 章节标题行 */}
                      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
                        <div className="flex-1 font-semibold text-slate-800 text-sm">
                          {editingOutlineNodeId === node.id ? (
                            <div className="flex items-center gap-2">
                              <span>{['第一章','第二章','第三章','第四章','第五章','第六章','第七章','第八章','第九章','第十章'][i] || `第${i+1}章`}</span>
                              <input
                                type="text"
                                value={editingOutlineNodeTitle}
                                onChange={(e) => setEditingOutlineNodeTitle(e.target.value)}
                                onBlur={() => handleSaveOutlineNodeTitle(node.id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveOutlineNodeTitle(node.id);
                                  } else if (e.key === 'Escape') {
                                    handleCancelEditOutlineNodeTitle();
                                  }
                                }}
                                className="flex-1 rounded border border-blue-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                autoFocus
                              />
                              {savingOutlineNodeId === node.id && (
                                <span className="text-xs text-blue-500">保存中...</span>
                              )}
                            </div>
                          ) : (
                            <span
                              className="cursor-pointer hover:text-blue-600 transition-colors group inline-block"
                              onClick={() => {
                                setEditingOutlineNodeId(node.id);
                                setEditingOutlineNodeTitle(node.title);
                              }}
                              title="点击编辑标题"
                            >
                              {['第一章','第二章','第三章','第四章','第五章','第六章','第七章','第八章','第九章','第十章'][i] || `第${i+1}章`} {node.title}
                              <span className="ml-1 text-xs text-slate-300 hidden group-hover:inline">✎</span>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="inline-flex items-center gap-1 text-[11px]">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="text-slate-500">已分配</span>
                            <span className="font-medium text-slate-700">{recommendedPages}页</span>
                            <span className="text-slate-400">/</span>
                            <span className="text-slate-700">{nodeWords.toLocaleString()}字</span>
                          </span>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:border-blue-300 hover:text-blue-600"
                          >
                            页数设置
                          </button>
                        </div>
                      </div>
                      
                      {/* 完整多级目录预览 */}
                      <div className="px-4 py-3 bg-white">
                        <div className="flex items-center justify-end mb-2">
                          <div className="inline-flex items-center gap-1 rounded bg-slate-50 px-2 py-0.5 text-[11px] text-slate-500 border border-slate-200">
                            本章可分配范围 <span className="font-medium text-slate-700">{minPages}-{maxPages}页</span>
                          </div>
                        </div>
                        
                        {/* 递归渲染多级子目录 */}
                        {renderChildren(node.children || [], 0)}
                      </div>
                      
                      {/* 底部操作按钮区 */}
                      <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50/30">
                        <div className="flex items-center gap-1">
                          {/* 上移章节（不是第一个才显示） */}
                          {i > 0 && (
                            <button
                              type="button"
                              onClick={() => moveNode(node.id, -1)}
                              className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-600 bg-white"
                              title="上移章节"
                            >
                              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="18 15 12 9 6 15" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          )}
                          {/* 下移章节（不是最后一个才显示） */}
                          {i < draft.length - 1 && (
                            <button
                              type="button"
                              onClick={() => moveNode(node.id, 1)}
                              className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-600 bg-white"
                              title="下移章节"
                            >
                              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="6 9 12 15 18 9" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          )}
                          {/* 添加子章节 */}
                          <button
                            type="button"
                            onClick={() => addChild(node.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:border-green-300 hover:text-green-600 bg-white"
                            title="添加子章节"
                          >
                            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round"/>
                              <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round"/>
                            </svg>
                          </button>
                          {/* 重排子目录编号 */}
                          <button
                            type="button"
                            onClick={() => {
                              setDraft((current) => reorderOutlineSubtree(current, node.id));
                              setDirty(true);
                            }}
                            className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-600 bg-white"
                            title="按当前顺序重排子目录编号"
                          >
                            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round"/>
                            </svg>
                          </button>
                          {/* 删除章节 */}
                          <button
                            type="button"
                            onClick={() => removeNode(node.id, node.title)}
                            className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600 bg-white"
                            title="删除本章节及其子目录"
                          >
                            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" strokeLinecap="round"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round"/>
                            </svg>
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={async () => {
                              // 找到对应的 idea
                              const idea = ideasData.find((it: any) => it.id === node.id);
                              const currentIdea = idea?.description || (node as any).merged_writing_idea || '';
                              const currentCatalog = node.children || [];
                              await openModal('ai_expand_catalog', node.id, node.title, currentIdea, currentCatalog, i);
                            }}
                            disabled={currentOperatingNodeId === node.id}
                            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-purple-500 to-indigo-600 px-3.5 py-1.5 text-xs font-medium text-white shadow-sm hover:from-purple-600 hover:to-indigo-700 disabled:opacity-50"
                          >
                            <SparklesIcon className="h-3.5 w-3.5" />
                            {currentOperatingNodeId === node.id ? '扩写中...' : 'AI扩写目录'}
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              // 找到对应的 idea
                              const idea = ideasData.find((it: any) => it.id === node.id);
                              const currentIdea = idea?.description || (node as any).merged_writing_idea || '';
                              const currentCatalog = node.children || [];
                              await openModal('ai_optimize_catalog', node.id, node.title, currentIdea, currentCatalog, i);
                            }}
                            disabled={currentOperatingNodeId === node.id}
                            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-purple-500 to-indigo-600 px-3.5 py-1.5 text-xs font-medium text-white shadow-sm hover:from-purple-600 hover:to-indigo-700 disabled:opacity-50"
                          >
                            <SparklesIcon className="h-3.5 w-3.5" />
                            {currentOperatingNodeId === node.id ? '优化中...' : 'AI优化目录'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 弹窗组件 */}
      {modalOpen && projectId && workspace && (
        <CatalogEditModal
          isOpen={modalOpen}
          onClose={closeModal}
          mode={modalMode}
          projectId={projectId}
          workspaceVersion={workspace.project_version}
          nodeId={modalNodeId}
          nodeTitle={modalNodeTitle}
          nodeIndex={modalNodeIndex}
          initialIdeaContent={modalInitialIdea}
          initialCatalog={modalInitialCatalog}
          ideaHistory={ideaHistory}
          catalogHistory={catalogHistory}
          onSaveIdea={handleModalSaveIdea}
          onGenerateIdea={handleModalGenerateIdea}
          onGenerateCatalog={handleModalGenerateCatalog}
          onInsertCatalog={handleModalInsertCatalog}
          onRestoreIdeaVersion={handleRestoreIdeaVersion}
          onRestoreCatalogVersion={handleRestoreCatalogVersion}
        />
      )}
    </div>
  );
};

export default DurableOutlineWorkspace;
